import { findSceneNode, getAllSceneNodes, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { AnimationConnection, AnimationGraph, AnimationGraphNode, AnimationGroupNumericOverride, AnimationGroupRegroupMode, AnimationProperty, AnimationTarget, AnimateAnimationNode, ColorAnimationSpace, GroupAnimationNode, MotionAnimationNode } from "@/document/model/animation";
import { DEFAULT_TRANSITION_COLOR, distancePinSnapBack, isClampedDistancePinTarget, isDistancePinTarget, isVectorSubElementAnimationTarget } from "@/document/model/animation";
import { hsbToOklch, hslToOklch, oklchToHsb, oklchToHsl, oklchToRgb, parseColor, rgbToOklch, type HsbColor, type HslColor, type OklchColor, type RgbColor } from "@/document/model/color";
import { createEmptyAnimationGraph, normalizeAnimationGraph } from "@/document/model/animation";
import { animationPropertyWithColorChannelValue, isColorAnimationProperty } from "@/document/animation/colorAnimationFields";
import { animationEasingFunction } from "@/document/animation/easing";
import { pointOnFollowPath, strokePathNetwork, tangentOnFollowPath } from "@/document/animation/followPath";
import { expandVectorAnimationTarget, expandedVectorPointPosition, type ExpandedVectorPoint } from "@/document/animation/vectorAnimationTargets";
import type { FrameNode, SceneNode, TextNode } from "@/document/model/nodes";
import { createIdentityTransform } from "@/document/model/nodes";
import { createDefaultStyle, normalizeStrokeStack, styleWithStrokeStack, type NodeEffect, type StrokeStyle } from "@/document/model/styles";
import type { RichTextModel, TextLineHeight, TypeStyle } from "@/document/model/text";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { contoursToPathData, pathDataToContours, transformContours } from "@/document/operations/pathGeometry";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { translateConnectedHandles } from "@/document/operations/vectorVertexMerge";
import { AnimCtxImpl } from "@/lib/anima/context";
import { timeline } from "@/lib/anima/timeline";
import type { Anchor, AnimCtx, BodyFn, GroupObject, ObjRef, SceneGeometry, SceneObject, TimelineAPI, Transform, Vec2Expr } from "@/lib/anima/types";
import type { Rect } from "@/lib/math/rect";
import { transformRotationOrigin } from "@/lib/math/rotation";
import { textVisualBounds } from "@/renderer/text/textVisualBounds";
import { getCachedTextLayout, type TextLayoutRun } from "@/renderer/text/canvasKitText";
import { verticalTextOffset } from "@/renderer/renderHelpers";

interface CurvaSceneObject extends SceneObject {
  readonly id: symbol;
  readonly nodeId: string;
}

export interface AnimationEvaluation {
  document: CurvaDocument;
  durationMs: number;
  displayFrameId?: string;
  displayFrameBounds?: Rect;
}

export interface AnimationMotionTiming {
  motion: MotionAnimationNode;
  startMs: number;
  endMs: number;
  durationMs: number;
  baseDurationMs: number;
  stutterTailMs: number;
  offsetBaseMs: number;
}

export interface AnimationTransitionTiming {
  timing: AnimationMotionTiming;
  targetAnimationNodeId: string;
  targetDurationMs: number;
}

export function animationDuration(graph: AnimationGraph | undefined, rootNodeId?: string, document?: CurvaDocument): number {
  return animationDurationInternal(normalizeAnimationGraph(graph ?? createEmptyAnimationGraph()), rootNodeId, new Set(), document);
}

function animationDurationInternal(graph: AnimationGraph, rootNodeId: string | undefined, seenRootIds: Set<string>, document?: CurvaDocument): number {
  const seenKey = rootNodeId ?? "__all__";
  if (seenRootIds.has(seenKey)) {
    return 0;
  }
  const nextSeen = new Set(seenRootIds).add(seenKey);
  const timings = animationMotionTimings(graph, rootNodeId, document);
  const baseDuration = timings.reduce((duration, timing) => Math.max(duration, timing.endMs), 0);
  return animationTransitionTimingsForGraph(graph, rootNodeId, nextSeen, document)
    .reduce((duration, transition) => Math.max(duration, transition.timing.endMs + transition.targetDurationMs), baseDuration);
}

export function animationTransitionTimings(graph: AnimationGraph | undefined, rootNodeId?: string, document?: CurvaDocument): AnimationTransitionTiming[] {
  return animationTransitionTimingsForGraph(normalizeAnimationGraph(graph ?? createEmptyAnimationGraph()), rootNodeId, new Set([rootNodeId ?? "__all__"]), document);
}

function animationTransitionTimingsForGraph(graph: AnimationGraph, rootNodeId: string | undefined, seenRootIds: Set<string>, document?: CurvaDocument): AnimationTransitionTiming[] {
  return animationMotionTimings(graph, rootNodeId, document).flatMap(timing => {
    const targetAnimationNodeId = transitionTargetAnimationNodeId(graph, timing.motion, seenRootIds);
    if (!targetAnimationNodeId) {
      return [];
    }
    const targetDurationMs = animationDurationInternal(graph, targetAnimationNodeId, seenRootIds, document);
    return targetDurationMs > 0 ? [{ timing, targetAnimationNodeId, targetDurationMs }] : [];
  });
}

export function animationMotionTimings(graph: AnimationGraph | undefined, rootNodeId?: string, document?: CurvaDocument): AnimationMotionTiming[] {
  const normalized = normalizeAnimationGraph(graph ?? createEmptyAnimationGraph());
  const motions = rootNodeId ? motionsForRoot(normalized, rootNodeId) : motionOrder(normalized);
  if (motions.length === 0) {
    return [];
  }

  const bodies = new Map<BodyFn<AnimCtx>, { motion: MotionAnimationNode; offsetBaseMs: number }>();
  let offsetBaseMs = 0;
  const built = timeline(t => {
    t.seq(() => {
      for (const motion of motions) {
        const body: BodyFn<AnimCtx> = () => {};
        bodies.set(body, { motion, offsetBaseMs });
        const durationMs = motionDurationMs(normalized, motion, document);
        scheduleMotionSegment(t, motion, durationMs, body, undefined, true);
        offsetBaseMs += motionCursorAdvanceMs(motion, durationMs);
      }
    });
  });

  return built.segs
    .flatMap(segment => {
      const timing = bodies.get(segment.body);
      return timing
        ? [{
            motion: timing.motion,
            startMs: segment.start,
            endMs: segment.end,
            durationMs: Math.max(0, segment.end - segment.start),
            baseDurationMs: motionBaseDurationMs(timing.motion),
            stutterTailMs: motionStutterTailMs(normalized, timing.motion, document),
            offsetBaseMs: timing.offsetBaseMs,
          }]
        : [];
    })
    .sort((left, right) => left.startMs - right.startMs || motions.indexOf(left.motion) - motions.indexOf(right.motion));
}

export function animationFrameId(graph: AnimationGraph | undefined, rootNodeId?: string): string | undefined {
  const normalized = normalizeAnimationGraph(graph ?? createEmptyAnimationGraph());
  if (rootNodeId) {
    const root = normalized.nodes.find(n => n.id === rootNodeId && n.kind === "animation");
    if (root?.kind === "animation" && root.frameId) return root.frameId;
  }
  const rootsWithFrames = normalized.nodes.filter(node => node.kind === "animation" && node.frameId);
  return rootsWithFrames[0]?.kind === "animation" ? rootsWithFrames[0].frameId : undefined;
}

export function documentWithAnimationAtTime(document: CurvaDocument, timeMs: number, rootNodeId?: string): AnimationEvaluation {
  return documentWithAnimationAtTimeInternal(document, timeMs, rootNodeId, new Set());
}

function documentWithAnimationAtTimeInternal(document: CurvaDocument, timeMs: number, rootNodeId: string | undefined, seenRootIds: Set<string>): AnimationEvaluation {
  const graph = normalizeAnimationGraph(document.animation ?? createEmptyAnimationGraph());
  const motions = rootNodeId ? motionsForRoot(graph, rootNodeId) : motionOrder(graph);
  const durationMs = animationDurationInternal(graph, rootNodeId, seenRootIds, document);
  const rootFrameId = animationFrameId(graph, rootNodeId);
  if (graph.nodes.length === 0 || durationMs <= 0) {
    return { document, durationMs, displayFrameId: rootFrameId };
  }

  const transition = activeTransitionAtTime(graph, rootNodeId, timeMs, seenRootIds, document);
  if (transition) {
    const beforeTransition = evaluateMotionsAtTime(document, graph, motions, transition.timing.endMs, transition.timing.endMs, rootFrameId);
    const targetEvaluation = documentWithAnimationAtTimeInternal(
      beforeTransition.document,
      timeMs - transition.timing.endMs,
      transition.targetAnimationNodeId,
      new Set(seenRootIds).add(rootNodeId ?? "__all__"),
    );
    const targetFrameId = targetEvaluation.displayFrameId ?? animationFrameId(graph, transition.targetAnimationNodeId);
    const sourceFrame = rootFrameId ? findSceneNode(beforeTransition.document, rootFrameId) : undefined;
    const displayFrame = targetFrameId ? findSceneNode(targetEvaluation.document, targetFrameId) : undefined;
    if (sourceFrame?.type === "frame" && displayFrame?.type === "frame" && displayFrame.id !== sourceFrame.id) {
      applyAnimatedBoundsToNode(displayFrame, sourceFrame.bounds);
    }
    return {
      document: targetEvaluation.document,
      durationMs,
      displayFrameId: targetFrameId,
      displayFrameBounds: sourceFrame?.type === "frame" ? { ...sourceFrame.bounds } : targetEvaluation.displayFrameBounds,
    };
  }

  const transitionEffect = activeTransitionEffectAtTime(graph, rootNodeId, timeMs, seenRootIds, document);
  if (transitionEffect) {
    const transitionMotion = transitionEffect.timing.motion;
    const sourceCurrent = evaluateMotionsAtTime(document, graph, motions, timeMs, transitionEffect.timing.endMs, rootFrameId);
    const sourceAtEnd = evaluateMotionsAtTime(document, graph, motions, transitionEffect.timing.endMs, transitionEffect.timing.endMs, rootFrameId);
    const targetEvaluation = documentWithAnimationAtTimeInternal(
      sourceAtEnd.document,
      0,
      transitionEffect.targetAnimationNodeId,
      new Set(seenRootIds).add(rootNodeId ?? "__all__"),
    );
    const targetFrameId = targetEvaluation.displayFrameId ?? animationFrameId(graph, transitionEffect.targetAnimationNodeId);
    const sourceFrame = rootFrameId ? findSceneNode(sourceCurrent.document, rootFrameId) : undefined;
    const targetFrame = targetFrameId ? findSceneNode(targetEvaluation.document, targetFrameId) : undefined;
    if (sourceFrame?.type === "frame" && targetFrame?.type === "frame") {
      const rawU = transitionEffect.timing.durationMs <= 0
        ? 1
        : (timeMs - transitionEffect.timing.startMs) / transitionEffect.timing.durationMs;
      const easedU = clamp01(animationEasingFunction(transitionMotion.easing)(clamp01(rawU)));
      const composed = transitionEffectDocument(
        sourceCurrent.document,
        sourceFrame,
        targetFrame,
        sourceFrame.bounds,
        transitionMotion,
        easedU,
      );
      return {
        document: composed.document,
        durationMs,
        displayFrameId: composed.frameId,
        displayFrameBounds: { ...sourceFrame.bounds },
      };
    }
  }

  return evaluateMotionsAtTime(document, graph, motions, timeMs, durationMs, rootFrameId);
}

function evaluateMotionsAtTime(
  document: CurvaDocument,
  graph: AnimationGraph,
  motions: MotionAnimationNode[],
  timeMs: number,
  durationMs: number,
  displayFrameId: string | undefined,
): AnimationEvaluation {
  const animated = structuredClone(document);
  const scene = new CurvaAnimationScene(animated);
  scene.prepareRegroupedTextTargets(graph);
  const ctx = new AnimCtxImpl();
  const built = timeline(t => {
    t.seq(() => {
      for (const motion of motions) {
        scheduleMotionEvaluation(t, graph, scene, motion);
      }
    });
  });

  ctx.reset(scene);
  built.evaluate(Math.max(0, Math.min(timeMs, durationMs)), ctx);
  ctx.resolveDeferredPins(scene);
  const solved = ctx.solve(scene);
  scene.applySolved(solved);
  scene.applyStagedStrokePaths();
  scene.applyStagedVectorMutations();

  const displayFrame = displayFrameId ? findSceneNode(animated, displayFrameId) : undefined;
  return {
    document: animated,
    durationMs,
    displayFrameId,
    displayFrameBounds: displayFrame?.type === "frame" ? { ...displayFrame.bounds } : undefined,
  };
}

function scheduleMotionSegment(
  t: TimelineAPI<AnimCtx>,
  motion: MotionAnimationNode,
  durationMs: number,
  body: BodyFn<AnimCtx>,
  instantBody?: (ctx: AnimCtx) => void,
  traceInstantAsTween = false,
  scheduleOverride?: () => void,
): void {
  const schedule = () => {
    if (scheduleOverride) {
      scheduleOverride();
      return;
    }
    const easing = animationEasingFunction(motion.easing);
    if (motion.instant) {
      if (traceInstantAsTween) {
        t.tween(durationMs, easing, body);
        return;
      }
      t.instant(instantBody ?? ((ctx: AnimCtx) => body(ctx, 1)));
    } else {
      t.tween(durationMs, easing, body);
    }
  };

  if (motion.offsetMs > 0) {
    t.wait(motion.offsetMs);
    schedule();
    return;
  }

  if (motion.offsetMs < 0) {
    t.overlap(motion.offsetMs, schedule);
    const advanceMs = motionCursorAdvanceMs(motion, durationMs);
    if (advanceMs > 0) {
      t.wait(advanceMs);
    }
    return;
  }

  schedule();
}

function scheduleMotionEvaluation(
  t: TimelineAPI<AnimCtx>,
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  motion: MotionAnimationNode,
): void {
  const baseDurationMs = motionBaseDurationMs(motion);
  const durationMs = motionDurationMs(graph, motion, scene.documentSnapshot());
  const stutterItems = stutterItemsForMotion(graph, scene, motion);
  const stutterMs = Math.max(0, motion.stutterMs);
  const animateItemsIndependently = stutterMs > 0 || (motion.animateElementsIndependently === true && motionHasGroupInput(graph, motion));
  if (!animateItemsIndependently || stutterItems.length <= 1) {
    const animateNodes = animateNodesForMotion(graph, motion);
    const body = (animCtx: AnimCtxImpl, u: number) => {
      const lookAtReferences = captureLookAtReferenceAngles(
        graph,
        scene,
        animCtx,
        animateNodes.map(node => ({ node })),
      );
      const pathOrientationReferences = capturePathOrientationReferenceAngles(graph, scene, animCtx, animateNodes);
      for (const animateNode of animateNodes) {
        applyAnimateNode(graph, scene, animCtx, animateNode, u, undefined, undefined, undefined, lookAtReferences, pathOrientationReferences);
      }
    };
    scheduleMotionSegment(
      t,
      motion,
      durationMs,
      (animCtx, u) => body(animCtx as AnimCtxImpl, u),
      animCtx => body(animCtx as AnimCtxImpl, 1),
    );
    return;
  }

  scheduleMotionSegment(
    t,
    motion,
    durationMs,
    () => {},
    undefined,
    false,
    () => {
      const easing = animationEasingFunction(motion.easing);
      t.par(() => {
        for (const [index, item] of stutterItems.entries()) {
          t.seq(() => {
            const delayMs = index * stutterMs;
            if (delayMs > 0) {
              t.wait(delayMs);
            }
            const body: BodyFn<AnimCtx> = (animCtx, u) => {
              const ctx = animCtx as AnimCtxImpl;
              const lookAtReferences = captureLookAtReferenceAngles(
                graph,
                scene,
                ctx,
                [{ node: item.node, refs: item.refs }],
              );
              const pathOrientationReferences = capturePathOrientationReferenceAngles(graph, scene, ctx, [item.node]);
              applyAnimateNode(graph, scene, ctx, item.node, u, item.refs, item.groupOverrideContext, item.vectorSubTargets, lookAtReferences, pathOrientationReferences);
            };
            if (motion.instant) {
              t.instant(animCtx => body(animCtx, 1));
            } else {
              t.tween(baseDurationMs, easing, body);
            }
          });
        }
      });
    },
  );
}

function motionBaseDurationMs(motion: MotionAnimationNode): number {
  return motion.instant ? 0 : Math.max(0, motion.durationMs);
}

function motionDurationMs(graph: AnimationGraph, motion: MotionAnimationNode, document?: CurvaDocument): number {
  return motionBaseDurationMs(motion) + motionStutterTailMs(graph, motion, document);
}

function motionStutterTailMs(graph: AnimationGraph, motion: MotionAnimationNode, document?: CurvaDocument): number {
  const stutterMs = Math.max(0, motion.stutterMs);
  if (stutterMs <= 0) {
    return 0;
  }
  const count = stutterItemCountForMotion(graph, motion, document);
  return count > 1 ? stutterMs * (count - 1) : 0;
}

function motionCursorAdvanceMs(motion: MotionAnimationNode, durationMs: number): number {
  if (motion.offsetMs >= 0) {
    return motion.offsetMs + durationMs;
  }
  return Math.max(0, motion.offsetMs + durationMs);
}

function transitionTargetAnimationNodeId(graph: AnimationGraph, motion: MotionAnimationNode, seenRootIds: Set<string>): string | undefined {
  if (!motion.transition || !motion.targetAnimationNodeId || seenRootIds.has(motion.targetAnimationNodeId)) {
    return undefined;
  }
  const target = graph.nodes.find(node => node.id === motion.targetAnimationNodeId);
  return target?.kind === "animation" ? target.id : undefined;
}

function activeTransitionAtTime(
  graph: AnimationGraph,
  rootNodeId: string | undefined,
  timeMs: number,
  seenRootIds: Set<string>,
  document?: CurvaDocument,
): AnimationTransitionTiming | undefined {
  return animationTransitionTimingsForGraph(graph, rootNodeId, new Set(seenRootIds).add(rootNodeId ?? "__all__"), document)
    .sort((left, right) => left.timing.endMs - right.timing.endMs)
    .find(transition => timeMs >= transition.timing.endMs);
}

function activeTransitionEffectAtTime(
  graph: AnimationGraph,
  rootNodeId: string | undefined,
  timeMs: number,
  seenRootIds: Set<string>,
  document?: CurvaDocument,
): AnimationTransitionTiming | undefined {
  return animationTransitionTimingsForGraph(graph, rootNodeId, new Set(seenRootIds).add(rootNodeId ?? "__all__"), document)
    .filter(transition => (transition.timing.motion.transitionEffect ?? "cut") !== "cut")
    .filter(transition => transition.timing.durationMs > 0)
    .sort((left, right) => left.timing.startMs - right.timing.startMs)
    .find(transition => timeMs >= transition.timing.startMs && timeMs < transition.timing.endMs);
}

function transitionEffectDocument(
  document: CurvaDocument,
  sourceFrame: FrameNode,
  targetFrame: FrameNode,
  displayBounds: Rect,
  motion: MotionAnimationNode,
  u: number,
): { document: CurvaDocument; frameId: string } {
  const animated = structuredClone(document);
  const source = structuredClone(sourceFrame);
  const target = structuredClone(targetFrame);
  applyAnimatedBoundsToNode(source, displayBounds);
  applyAnimatedBoundsToNode(target, displayBounds);

  const effect = motion.transitionEffect ?? "cut";
  const children: SceneNode[] = [];
  if (effect === "crossfade") {
    children.push(frameWithOpacity(source, 1 - u), frameWithOpacity(target, u));
  } else if (effect === "slideLeft" || effect === "slideRight" || effect === "slideUp" || effect === "slideDown") {
    children.push(source, slideFrame(target, displayBounds, effect, u));
  } else if (effect === "zoom") {
    children.push(source, frameWithOpacity(zoomFrame(target, displayBounds, u), u));
  } else if (effect === "push") {
    children.push(offsetFrame(source, -displayBounds.width * u, 0), offsetFrame(target, displayBounds.width * (1 - u), 0));
  } else if (effect === "wipe") {
    children.push(source, wipeFrame(target, displayBounds, u));
  } else if (effect === "fadeThroughColor") {
    const sourceOpacity = 1 - clamp01(u * 2);
    const targetOpacity = clamp01((u - 0.5) * 2);
    const colorOpacity = u < 0.5 ? u * 2 : 2 - u * 2;
    children.push(
      frameWithOpacity(source, sourceOpacity),
      colorFrame(displayBounds, motion.transitionColor ?? DEFAULT_TRANSITION_COLOR, colorOpacity),
      frameWithOpacity(target, targetOpacity),
    );
  } else {
    children.push(source);
  }

  const compositeFrame: FrameNode = {
    id: `transition-preview-${motion.id}`,
    name: "Transition",
    type: "frame",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { ...displayBounds },
    style: { ...createDefaultStyle("#ffffff"), fills: [], opacity: 1 },
    clipsContent: true,
    children,
  };

  animated.pages[0]?.children.push(compositeFrame);
  return { document: animated, frameId: compositeFrame.id };
}

function frameWithOpacity<T extends FrameNode>(frame: T, opacity: number): T {
  return {
    ...frame,
    style: { ...frame.style, opacity: clamp01((frame.style.opacity ?? 1) * opacity) },
  };
}

function slideFrame(frame: FrameNode, bounds: Rect, effect: NonNullable<MotionAnimationNode["transitionEffect"]>, u: number): FrameNode {
  const dx = effect === "slideLeft" ? bounds.width * (1 - u) : effect === "slideRight" ? -bounds.width * (1 - u) : 0;
  const dy = effect === "slideUp" ? bounds.height * (1 - u) : effect === "slideDown" ? -bounds.height * (1 - u) : 0;
  return offsetFrame(frame, dx, dy);
}

function offsetFrame<T extends FrameNode>(frame: T, dx: number, dy: number): T {
  const next = structuredClone(frame);
  applyAnimatedBoundsToNode(next, { ...next.bounds, x: next.bounds.x + dx, y: next.bounds.y + dy });
  return next;
}

function zoomFrame<T extends FrameNode>(frame: T, bounds: Rect, u: number): T {
  const scale = 0.92 + 0.08 * u;
  const width = bounds.width * scale;
  const height = bounds.height * scale;
  const next = structuredClone(frame);
  applyAnimatedBoundsToNode(next, {
    x: bounds.x + (bounds.width - width) / 2,
    y: bounds.y + (bounds.height - height) / 2,
    width,
    height,
  });
  return next;
}

function wipeFrame(frame: FrameNode, bounds: Rect, u: number): FrameNode {
  const wrapper: FrameNode = {
    id: `${frame.id}-wipe`,
    name: "Wipe",
    type: "frame",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { ...bounds, width: bounds.width * clamp01(u) },
    style: { ...createDefaultStyle("#ffffff"), fills: [], opacity: 1 },
    clipsContent: true,
    children: [frame],
  };
  return wrapper;
}

function colorFrame(bounds: Rect, color: MotionAnimationNode["transitionColor"], opacity: number): FrameNode {
  return {
    id: "transition-color",
    name: "Color",
    type: "frame",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { ...bounds },
    style: { ...createDefaultStyle(color ?? DEFAULT_TRANSITION_COLOR), opacity: clamp01(opacity) },
    clipsContent: false,
    children: [],
  };
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function interpolateAnimatedColor(
  from: OklchColor,
  to: OklchColor,
  colorSpace: ColorAnimationSpace,
  channels: string[],
  u: number,
): OklchColor {
  const activeChannels = new Set(channels.length > 0 ? channels : defaultColorChannels(colorSpace));
  switch (colorSpace) {
    case "rgb": {
      const start = oklchToRgb(from);
      const end = oklchToRgb(to);
      return rgbToOklch({
        r: channelLerp("r", start.r, end.r, activeChannels, u),
        g: channelLerp("g", start.g, end.g, activeChannels, u),
        b: channelLerp("b", start.b, end.b, activeChannels, u),
        alpha: channelLerp("alpha", start.alpha, end.alpha, activeChannels, u),
      } satisfies RgbColor);
    }
    case "hsl": {
      const start = oklchToHsl(from);
      const end = oklchToHsl(to);
      return hslToOklch({
        h: hueChannelLerp("h", start.h, end.h, activeChannels, u),
        s: channelLerp("s", start.s, end.s, activeChannels, u),
        l: channelLerp("l", start.l, end.l, activeChannels, u),
        alpha: channelLerp("alpha", start.alpha, end.alpha, activeChannels, u),
      } satisfies HslColor);
    }
    case "hsb": {
      const start = oklchToHsb(from);
      const end = oklchToHsb(to);
      return hsbToOklch({
        h: hueChannelLerp("h", start.h, end.h, activeChannels, u),
        s: channelLerp("s", start.s, end.s, activeChannels, u),
        b: channelLerp("b", start.b, end.b, activeChannels, u),
        alpha: channelLerp("alpha", start.alpha, end.alpha, activeChannels, u),
      } satisfies HsbColor);
    }
    case "oklch":
    default: {
      const start = parseColor(from);
      const end = parseColor(to);
      return parseColor({
        l: channelLerp("l", start.l, end.l, activeChannels, u),
        c: channelLerp("c", start.c, end.c, activeChannels, u),
        h: hueChannelLerp("h", start.h, end.h, activeChannels, u),
        alpha: channelLerp("alpha", start.alpha, end.alpha, activeChannels, u),
      });
    }
  }
}

function defaultColorChannels(colorSpace: ColorAnimationSpace): string[] {
  switch (colorSpace) {
    case "rgb":
      return ["r", "g", "b"];
    case "hsl":
      return ["h", "s", "l"];
    case "hsb":
      return ["h", "s", "b"];
    case "oklch":
    default:
      return ["l", "c", "h"];
  }
}

function channelLerp(channel: string, from: number, to: number, channels: Set<string>, u: number): number {
  return channels.has(channel) ? lerpNumber(from, to, u) : from;
}

function hueChannelLerp(channel: string, from: number, to: number, channels: Set<string>, u: number): number {
  if (!channels.has(channel)) {
    return from;
  }
  return from + shortestHueDelta(from, to) * clamp01(u);
}

function interpolateEffectValue(
  effect: NodeEffect,
  field: Extract<AnimationProperty, { kind: "effectValue" }>["field"],
  value: number,
  u: number,
): NodeEffect {
  const nextValue = (from: number) => lerpNumber(from, value, u);
  if (field === "opacity") {
    return "color" in effect
      ? { ...effect, color: { ...effect.color, alpha: clamp01(nextValue(effect.color.alpha * 100) / 100) } }
      : effect;
  }
  if (field === "blur" && "blur" in effect) {
    return { ...effect, blur: Math.max(0, nextValue(effect.blur)) };
  }
  if (field === "spread" && "spread" in effect) {
    return { ...effect, spread: nextValue(effect.spread) };
  }
  if (field === "offsetX" && "offsetX" in effect) {
    return { ...effect, offsetX: nextValue(effect.offsetX) };
  }
  if (field === "offsetY" && "offsetY" in effect) {
    return { ...effect, offsetY: nextValue(effect.offsetY) };
  }
  return effect;
}

function lerpNumber(from: number, to: number, u: number): number {
  return from + (to - from) * clamp01(u);
}

function shortestHueDelta(from: number, to: number): number {
  const delta = (((to - from) % 360) + 360) % 360;
  return delta > 180 ? delta - 360 : delta;
}

interface GroupOverrideContext {
  index: number;
  count: number;
}

type LookAtReferenceAngles = Map<string, number>;
type PathOrientationReferenceAngles = Map<string, number>;

function lookAtReferenceKey(nodeId: string, propertyIndex: number): string {
  return `${nodeId}:${propertyIndex}`;
}

function pathOrientationReferenceKey(nodeId: string, propertyIndex: number): string {
  return `${nodeId}:${propertyIndex}`;
}

function captureLookAtReferenceAngles(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  items: Array<{ node: AnimateAnimationNode; refs?: ObjRef[] }>,
): LookAtReferenceAngles {
  const references: LookAtReferenceAngles = new Map();
  if (!items.some(item => item.node.properties.some(property => property.kind === "lookAt"))) {
    return references;
  }

  // Earlier timeline segments may leave position expressions deferred. Commit
  // those before measuring this motion's reference vectors.
  ctx.resolveDeferredPins(scene);
  for (const item of items) {
    const sourceTarget = animationTargetForAnimate(graph, item.node);
    const refs = item.refs ?? targetRefs(graph, scene, sourceTarget);
    if (refs.length === 0) {
      continue;
    }
    const source = refs.length > 1 ? scene.groupFor(refs) : refs[0]!;
    const sourceCenter = ctx.anchorPosition(source, "center");
    for (const [propertyIndex, property] of item.node.properties.entries()) {
      if (property.kind !== "lookAt") {
        continue;
      }
      const target = bboxTargetRef(graph, scene, lookAtTargetForAnimate(graph, item.node, property, propertyIndex));
      if (!target) {
        continue;
      }
      const targetCenter = ctx.anchorPosition(target, "center");
      const dx = targetCenter[0] - sourceCenter[0];
      const dy = targetCenter[1] - sourceCenter[1];
      if (Math.hypot(dx, dy) <= 1e-9) {
        continue;
      }
      references.set(lookAtReferenceKey(item.node.id, propertyIndex), Math.atan2(dy, dx));
    }
  }
  return references;
}

function capturePathOrientationReferenceAngles(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  nodes: AnimateAnimationNode[],
): PathOrientationReferenceAngles {
  const references: PathOrientationReferenceAngles = new Map();
  if (!nodes.some(node => node.properties.some(property => property.kind === "followPath" && property.orientToPath))) {
    return references;
  }

  ctx.resolveDeferredPins(scene);
  for (const node of nodes) {
    for (const [propertyIndex, property] of node.properties.entries()) {
      if (property.kind !== "followPath" || !property.orientToPath) {
        continue;
      }
      const pathRef = pathObjectRefForTarget(graph, scene, followPathTargetForAnimate(graph, node, property, propertyIndex));
      if (!pathRef) {
        continue;
      }
      const angle = followPathWorldTangentAngle(ctx, scene, pathRef, 0);
      if (angle !== undefined) {
        references.set(pathOrientationReferenceKey(node.id, propertyIndex), angle);
      }
    }
  }
  return references;
}

function applyAnimateNode(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  u: number,
  overrideRefs?: ObjRef[],
  groupOverrideContext?: GroupOverrideContext,
  overrideVectorSubTargets?: VectorSubElementTarget[],
  lookAtReferences?: LookAtReferenceAngles,
  pathOrientationReferences?: PathOrientationReferenceAngles,
): void {
  const target = animationTargetForAnimate(graph, node);
  if (isVectorSubElementAnimationTarget(target)) {
    applyVectorSubElementAnimateNode(graph, scene, ctx, node, target, u, groupOverrideContext);
    return;
  }

  // When a stutter/independent item explicitly carries its own targets, apply only those:
  // - overrideVectorSubTargets → this item animates specific vector sub-elements
  // - overrideRefs → this item animates a specific whole-node member (skip vector subs)
  // Otherwise (non-stutter) collect and apply every vector sub-element in the group.
  const vectorSubTargets = overrideVectorSubTargets ?? (overrideRefs ? [] : collectVectorSubElementTargets(graph, target));
  for (const [index, subTarget] of vectorSubTargets.entries()) {
    applyVectorSubElementAnimateNode(
      graph,
      scene,
      ctx,
      node,
      subTarget,
      u,
      groupOverrideContext ?? { index, count: vectorSubTargets.length },
    );
  }

  if (overrideVectorSubTargets) {
    return;
  }

  const refs = overrideRefs ?? targetRefs(graph, scene, target);
  if (refs.length === 0) {
    return;
  }
  const frameBounds = scene.frameBoundsForAnimate(graph, node, refs);
  const selection = ctx.select(...refs);
  const layoutSelection = ctx.select(...expandGroupRefs(refs));
  const properties = node.properties.map((property, propertyIndex) => ({
    property: propertyWithGroupOverrides(property, groupOverrideContext),
    propertyIndex,
  }));
  // Rotation-following behaviors consume the solved result of every positional
  // constraint in this behavior, so apply them after ordinary properties.
  for (const { property, propertyIndex } of properties.filter(entry => entry.property.kind !== "lookAt")) {
    applyProperty(graph, scene, ctx, node, refs, selection, layoutSelection, property, propertyIndex, frameBounds, u);
  }
  for (const { property, propertyIndex } of properties) {
    if (property.kind !== "followPath" || !property.orientToPath) {
      continue;
    }
    const referenceAngle = pathOrientationReferences?.get(pathOrientationReferenceKey(node.id, propertyIndex));
    if (referenceAngle !== undefined) {
      applyFollowPathOrientation(graph, scene, ctx, node, refs, selection, property, propertyIndex, u, referenceAngle);
    }
  }
  // Look At remains the final directional constraint when both are present.
  for (const { property, propertyIndex } of properties.filter(entry => entry.property.kind === "lookAt")) {
    applyProperty(
      graph,
      scene,
      ctx,
      node,
      refs,
      selection,
      layoutSelection,
      property,
      propertyIndex,
      frameBounds,
      u,
      lookAtReferences?.get(lookAtReferenceKey(node.id, propertyIndex)),
    );
  }
}

function propertyWithGroupOverrides(property: AnimationProperty, context: GroupOverrideContext | undefined): AnimationProperty {
  if (!context || context.count <= 0 || !property.groupOverrides) {
    return property;
  }
  let next = property;
  for (const [field, override] of Object.entries(property.groupOverrides)) {
    const value = groupOverrideValue(override, context.index, context.count);
    if (value !== undefined) {
      next = propertyWithNumericFieldValue(next, field, value);
    }
  }
  return next;
}

function groupOverrideValue(override: AnimationGroupNumericOverride, index: number, count: number): number | undefined {
  if (override.kind === "distributed") {
    const t = count <= 1 ? 0 : index / (count - 1);
    return lerpNumber(override.from, override.to, t);
  }
  if (override.kind === "random") {
    return lerpNumber(override.from, override.to, seededUnitRandom(override.seed, index));
  }
  const values = parseNumericOverrideList(override.values);
  if (values.length === 0) {
    return undefined;
  }
  return values[Math.min(index, values.length - 1)];
}

function parseNumericOverrideList(values: string): number[] {
  return values
    .split(",")
    .map(value => Number.parseFloat(value.trim()))
    .filter(Number.isFinite);
}

function seededUnitRandom(seed: number, index: number): number {
  let value = (Math.imul(seed | 0, 1664525) + Math.imul(index + 1, 1013904223)) | 0;
  value ^= value >>> 16;
  value = Math.imul(value, 2246822507) | 0;
  value ^= value >>> 13;
  value = Math.imul(value, 3266489909) | 0;
  value ^= value >>> 16;
  return (value >>> 0) / 4294967295;
}

function propertyWithNumericFieldValue(property: AnimationProperty, field: string, displayValue: number): AnimationProperty {
  if (property.kind === "pin") {
    if (field === "x") return { ...property, x: displayValue };
    if (field === "y") return { ...property, y: displayValue };
    if (field === "target.progress" && property.target?.kind === "pathPercent") {
      return { ...property, target: { ...property.target, progress: clamp01(displayValue / 100) } };
    }
    if (field === "target.distance" && isDistancePinTarget(property.target)) {
      return { ...property, target: { ...property.target, distance: Math.max(0, displayValue) } };
    }
    return property;
  }
  if (property.kind === "followPath" && field === "progress") {
    return { ...property, progress: clamp01(displayValue / 100) };
  }
  if (property.kind === "strokePath" && field === "progress") {
    return { ...property, progress: clamp01(displayValue / 100) };
  }
  if (property.kind === "opacity" && field === "value") {
    return { ...property, value: clamp01(displayValue / 100) };
  }
  if ((property.kind === "layout" || property.kind === "stack" || property.kind === "inline") && field === "gap") {
    return { ...property, gap: displayValue };
  }
  if (property.kind === "fitToBBox" && field === "pad") {
    return { ...property, pad: displayValue };
  }
  if (property.kind === "orbit" && field === "angle") {
    return { ...property, angle: displayValue };
  }
  if (property.kind === "effectValue" && field === "value") {
    return { ...property, value: displayValue };
  }
  if (isColorAnimationProperty(property)) {
    return animationPropertyWithColorChannelValue(property, field, displayValue);
  }
  if ("value" in property && field === "value") {
    return { ...property, value: displayValue };
  }
  return property;
}

function applyProperty(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  refs: ObjRef[],
  selection: ReturnType<AnimCtxImpl["select"]>,
  layoutSelection: ReturnType<AnimCtxImpl["select"]>,
  property: AnimationProperty,
  propertyIndex: number,
  frameBounds: { x: number; y: number; width: number; height: number },
  u: number,
  lookAtReferenceAngle?: number,
): void {
  switch (property.kind) {
    case "opacity":
      selection.opacity(normalizeOpacityValue(property.value), u);
      break;
    case "pin":
      if (property.target?.kind === "offset") {
        selection.pinBy(property.anchor as Anchor, [property.x, property.y], u);
      } else if (isDistancePinTarget(property.target)) {
        const targetPoint = pinTargetPoint(graph, scene, ctx, node, property, propertyIndex, frameBounds);
        if (isClampedDistancePinTarget(property.target) && !distancePinSnapBack(property.target)) {
          // Persist displacements along the dependency's path so far (no snap-back).
          // Min distance is already time-correct from how far the dependency has
          // traveled, so apply it fully. Max distance can be violated at rest and
          // would jump on frame 0 without easing by motion progress.
          selection.pin(
            "center",
            pointAtIntegratedDistanceExpr(
              selection.bbox().center(),
              initialDistanceDependencyCenter(graph, scene, node, property, propertyIndex, frameBounds),
              targetPoint,
              property.target.distance,
              property.target.kind,
            ),
            property.target.kind === "maxDistance" ? u : 1,
          );
        } else {
          selection.pin(
            "center",
            pointAtDistanceExpr(
              selection.bbox().center(),
              targetPoint,
              property.target.distance,
              property.target.kind,
            ),
            u,
          );
        }
      } else {
        selection.pin(property.anchor as Anchor, pinTargetPoint(graph, scene, ctx, node, property, propertyIndex, frameBounds), u);
      }
      break;
    case "followPath": {
      const point = followPathPointExpr(graph, scene, ctx, node, property, propertyIndex, u);
      if (point) {
        selection.pin(property.anchor as Anchor, point, 1);
      }
      break;
    }
    case "strokePath":
      scene.applyStrokePath(refs, scaledPathProgress(u, property.progress), property.inverse, property.erase);
      break;
    case "orbit": {
      const orbitTarget = bboxTargetRef(graph, scene, orbitTargetForAnimate(graph, node, property, propertyIndex));
      if (orbitTarget) {
        selection.pin("center", orbitTargetPoint(ctx, selection, orbitTarget, property.angle, u), 1);
      }
      break;
    }
    case "lookAt": {
      const lookAtTarget = bboxTargetRef(graph, scene, lookAtTargetForAnimate(graph, node, property, propertyIndex));
      if (lookAtTarget && lookAtReferenceAngle !== undefined) {
        applyLookAt(scene, ctx, refs, selection, lookAtTarget, lookAtReferenceAngle);
      }
      break;
    }
    case "scale":
      selection.scale(property.value, u);
      break;
    case "scaleX":
      selection.scaleX(property.value, u);
      break;
    case "scaleY":
      selection.scaleY(property.value, u);
      break;
    case "rotation":
      scene.markAnimatedRotation(refs);
      pinSingleObjectRotationCenter(scene, ctx, refs);
      selection.rotate((property.value * Math.PI) / 180, u);
      break;
    case "width":
      selection.width(property.value, u);
      break;
    case "height":
      selection.height(property.value, u);
      break;
    case "layout":
      layoutSelection.layout({ axis: property.axis, gap: property.gap }, u);
      break;
    case "stack":
      layoutSelection.layout({ axis: "y", align: "center", gap: property.gap }, u);
      break;
    case "inline":
      layoutSelection.layout({ axis: "x", align: "baselineLeft", gap: property.gap }, u);
      break;
    case "fitToBBox": {
      const fitTarget = bboxTargetRef(graph, scene, fitToBBoxTargetForAnimate(graph, node, property, propertyIndex));
      if (fitTarget) {
        selection.fitToBBox(fitTarget, u, { pad: property.pad });
      }
      break;
    }
    case "prop":
      selection.prop(property.name, property.value, u);
      break;
    case "fillColor":
      scene.applyFillColor(refs, property.fillIndex, property.colorSpace, property.channels, property.color, u);
      break;
    case "strokeColor":
      scene.applyStrokeColor(refs, property.strokeIndex, property.colorSpace, property.channels, property.color, u);
      break;
    case "effectValue":
      scene.applyEffectValue(refs, property.effectIndex, property.field, property.value, u);
      break;
    case "effectColor":
      scene.applyEffectColor(refs, property.effectIndex, property.colorSpace, property.channels, property.color, u);
      break;
  }
}

function applyVectorSubElementAnimateNode(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  target: Extract<AnimationTarget, { kind: "vectorVertex" | "vectorHandle" | "vectorEdge" }>,
  u: number,
  groupOverrideContext?: GroupOverrideContext,
): void {
  for (const [propertyIndex, property] of node.properties.entries()) {
    const resolvedProperty = propertyWithGroupOverrides(property, groupOverrideContext);
    if (resolvedProperty.kind === "pin") {
      applyVectorPinProperty(graph, scene, ctx, node, target, resolvedProperty, propertyIndex, u);
    }
  }
}

function applyVectorPinProperty(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  target: Extract<AnimationTarget, { kind: "vectorVertex" | "vectorHandle" | "vectorEdge" }>,
  property: Extract<AnimationProperty, { kind: "pin" }>,
  propertyIndex: number,
  u: number,
): void {
  const expanded = expandVectorAnimationTarget(scene.documentSnapshot(), target);
  if (expanded.length === 0) {
    return;
  }

  const vectorNode = scene.sceneNodeById(target.nodeId);
  if (vectorNode?.type !== "vectorNetwork") {
    return;
  }

  const refs = scene.objectRefsForNodeId(target.nodeId);
  const frameBounds = scene.frameBoundsForAnimate(graph, node, refs);
  const vertexPoints = expanded.filter((point): point is Extract<ExpandedVectorPoint, { kind: "vertex" }> => point.kind === "vertex");

  if (vertexPoints.length > 1) {
    const targetInitial = initialDistanceDependencyCenter(graph, scene, node, property, propertyIndex, frameBounds);
    applyVectorRigidPin(scene, vectorNode.network, vertexPoints, property, () =>
      resolvePinTargetPoint(graph, scene, ctx, node, property, propertyIndex, frameBounds),
    targetInitial,
    u);
    return;
  }

  const point = expanded[0]!;
  const current = expandedVectorPointPosition(vectorNode.network, point);
  if (!current) {
    return;
  }

  const targetInitial = initialDistanceDependencyCenter(graph, scene, node, property, propertyIndex, frameBounds);
  const next = vectorPinInterpolatedPoint(current, property, () =>
    resolvePinTargetPoint(graph, scene, ctx, node, property, propertyIndex, frameBounds),
  targetInitial,
  u);

  if (point.kind === "vertex") {
    scene.stageVectorVertexMove(point.nodeId, point.vertexId, next.x, next.y, point.translateConnectedHandles);
    return;
  }

  scene.stageVectorHandleMove(point.nodeId, point.edgeId, point.endpoint, next.x, next.y);
}

function applyVectorRigidPin(
  scene: CurvaAnimationScene,
  network: import("@/document/model/vectorNetwork").VectorNetwork,
  vertexPoints: Array<Extract<ExpandedVectorPoint, { kind: "vertex" }>>,
  property: Extract<AnimationProperty, { kind: "pin" }>,
  targetPoint: () => [number, number],
  targetInitial: readonly [number, number],
  u: number,
): void {
  const currentPositions = vertexPoints.flatMap(point => {
    const position = expandedVectorPointPosition(network, point);
    return position ? [position] : [];
  });
  if (currentPositions.length === 0) {
    return;
  }

  const anchor = anchorPointForPositions(currentPositions, isDistancePinTarget(property.target) ? "center" : property.anchor);
  const nextAnchor = vectorPinInterpolatedPoint(anchor, property, targetPoint, targetInitial, u);
  const dx = nextAnchor.x - anchor.x;
  const dy = nextAnchor.y - anchor.y;
  if (dx === 0 && dy === 0) {
    return;
  }

  for (const point of vertexPoints) {
    const current = expandedVectorPointPosition(network, point);
    if (!current) {
      continue;
    }
    scene.stageVectorVertexMove(
      point.nodeId,
      point.vertexId,
      current.x + dx,
      current.y + dy,
      point.translateConnectedHandles,
    );
  }
}

function vectorPinInterpolatedPoint(
  current: { x: number; y: number },
  property: Extract<AnimationProperty, { kind: "pin" }>,
  targetPoint: () => [number, number],
  targetInitial: readonly [number, number],
  u: number,
): { x: number; y: number } {
  if (property.target?.kind === "offset") {
    return {
      x: current.x + property.x * u,
      y: current.y + property.y * u,
    };
  }

  const [targetX, targetY] = targetPoint();
  if (isDistancePinTarget(property.target)) {
    if (isClampedDistancePinTarget(property.target) && !distancePinSnapBack(property.target)) {
      const destination = integrateDistanceConstraint(
        [current.x, current.y],
        targetInitial,
        [targetX, targetY],
        property.target.distance,
        property.target.kind,
      );
      const progress = property.target.kind === "maxDistance" ? u : 1;
      return {
        x: lerpNumber(current.x, destination[0], progress),
        y: lerpNumber(current.y, destination[1], progress),
      };
    }
    const destination = pointAtDistance(
      [current.x, current.y],
      [targetX, targetY],
      property.target.distance,
      property.target.kind,
    );
    return {
      x: lerpNumber(current.x, destination[0], u),
      y: lerpNumber(current.y, destination[1], u),
    };
  }
  return {
    x: lerpNumber(current.x, targetX, u),
    y: lerpNumber(current.y, targetY, u),
  };
}

function resolvePinTargetPoint(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  property: Extract<AnimationProperty, { kind: "pin" }>,
  propertyIndex: number,
  frameBounds: { x: number; y: number; width: number; height: number },
): [number, number] {
  const targetPoint = pinTargetPoint(graph, scene, ctx, node, property, propertyIndex, frameBounds);
  const point = typeof targetPoint === "function" ? targetPoint(ctx.solve(scene)) : targetPoint;
  return [point[0], point[1]];
}

function anchorPointForPositions(
  positions: Array<{ x: number; y: number }>,
  anchor: Extract<AnimationProperty, { kind: "pin" }>["anchor"],
): { x: number; y: number } {
  const xs = positions.map(position => position.x);
  const ys = positions.map(position => position.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = maxX - minX;
  const height = maxY - minY;
  const [x, y] = frameAnchorPoint({ x: minX, y: minY, width, height }, anchor as Anchor);
  return { x, y };
}

function pinSingleObjectRotationCenter(scene: CurvaAnimationScene, ctx: AnimCtxImpl, refs: ObjRef[]): void {
  if (refs.length !== 1 || ("isGroup" in refs[0]! && refs[0]!.isGroup)) {
    return;
  }
  const ref = refs[0]! as SceneObject;
  // A position behavior (pin/offset/distance/orbit) may already fix this object's
  // placement. Its pin — possibly still deferred — defines where the center lands,
  // and solve() rotates around that pinned anchor. Adding our own center pin here
  // would prematurely resolve and overwrite that position pin (e.g. orbit), so let
  // the existing constraint own placement and only spin in place.
  if (ctx.hasPositionConstraint(ref)) {
    return;
  }
  const transform = ctx.solve(scene).getTransform(ref);
  const scaleX = (transform.scaleX ?? 1) * transform.scale;
  const scaleY = (transform.scaleY ?? 1) * transform.scale;
  const center = transformLocalPoint(transform, scene.resolveAnchor(ref, "center"), scaleX, scaleY);
  ctx.pin(ref, "center").to([center.x, center.y], 1);
}

function targetRefs(graph: AnimationGraph, scene: CurvaAnimationScene, target: AnimationTarget | undefined): ObjRef[] {
  if (!target) {
    return [];
  }
  if (target.kind === "sceneNode") {
    return scene.refsForSceneNodeTarget(target.nodeId);
  }
  if (isVectorSubElementAnimationTarget(target)) {
    return [];
  }
  const node = graph.nodes.find(candidate => candidate.id === target.nodeId);
  if (node?.kind !== "group") {
    return [];
  }
  return groupTargets(graph, scene, node);
}

function expandGroupRefs(refs: ObjRef[]): ObjRef[] {
  const expanded: ObjRef[] = [];
  for (const ref of refs) {
    if ("isGroup" in ref && ref.isGroup) {
      expanded.push(...expandGroupRefs(ref.members));
      continue;
    }
    expanded.push(ref);
  }
  return expanded;
}

type VectorSubElementTarget = Extract<AnimationTarget, { kind: "vectorVertex" | "vectorHandle" | "vectorEdge" }>;

function vectorSubElementTargetKey(target: VectorSubElementTarget): string {
  switch (target.kind) {
    case "vectorVertex": return `vectorVertex:${target.nodeId}:${target.vertexId}`;
    case "vectorHandle": return `vectorHandle:${target.nodeId}:${target.edgeId}:${target.endpoint}`;
    case "vectorEdge": return `vectorEdge:${target.nodeId}:${target.edgeId}`;
  }
}

/**
 * Collect the vector sub-element targets reachable from an animate node's target,
 * including targets nested inside group nodes (and their upstream group inputs).
 * Whole scene-node targets are handled separately via ObjRefs.
 */
function collectVectorSubElementTargets(
  graph: AnimationGraph,
  target: AnimationTarget | undefined,
  seenGroups: Set<string> = new Set(),
  collectedKeys: Set<string> = new Set(),
  out: VectorSubElementTarget[] = [],
): VectorSubElementTarget[] {
  if (!target) {
    return out;
  }
  if (isVectorSubElementAnimationTarget(target)) {
    const key = vectorSubElementTargetKey(target);
    if (!collectedKeys.has(key)) {
      collectedKeys.add(key);
      out.push(target);
    }
    return out;
  }
  if (target.kind !== "animationNode") {
    return out;
  }
  const node = graph.nodes.find(candidate => candidate.id === target.nodeId);
  if (node?.kind !== "group" || seenGroups.has(node.id)) {
    return out;
  }
  seenGroups.add(node.id);
  for (const groupTarget of node.targets) {
    collectVectorSubElementTargets(graph, groupTarget, seenGroups, collectedKeys, out);
  }
  for (const connection of graph.connections) {
    if (connection.to.nodeId !== node.id) {
      continue;
    }
    const source = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
    if (source?.kind === "group") {
      collectVectorSubElementTargets(graph, { kind: "animationNode", nodeId: source.id }, seenGroups, collectedKeys, out);
    }
  }
  return out;
}

function groupTargets(graph: AnimationGraph, scene: CurvaAnimationScene, node: GroupAnimationNode): ObjRef[] {
  const regroupedRefs = regroupedTextTargets(scene, node);
  const refs = regroupedRefs ?? node.targets.flatMap(target => targetRefs(graph, scene, target));
  const upstreamGroups = graph.connections
    .filter(connection => connection.to.nodeId === node.id)
    .flatMap(connection => {
      const source = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
      return source?.kind === "group" ? groupTargets(graph, scene, source) : [];
    });
  const merged = scene.uniqueRefsInOrder([...refs, ...upstreamGroups]);
  return merged.length > 1 ? [scene.groupFor(merged)] : merged;
}

function regroupedTextTargets(scene: CurvaAnimationScene, node: GroupAnimationNode): ObjRef[] | undefined {
  if (node.regroup === "none" || node.targets.length !== 1) {
    return undefined;
  }
  const target = node.targets[0];
  if (target?.kind !== "sceneNode") {
    return undefined;
  }
  return scene.regroupTextTarget(target.nodeId, node.regroup, node.regroupOrder);
}

function animationTargetForAnimate(graph: AnimationGraph, node: AnimateAnimationNode): AnimationTarget | undefined {
  const input = graph.connections.find(connection => connection.to.nodeId === node.id && (connection.to.pin === "input" || connection.to.pin === "objectInput"));
  if (input) {
    return { kind: "animationNode", nodeId: input.from.nodeId };
  }
  return node.target;
}

function dependencyConnectionForAnimateProperty(graph: AnimationGraph, node: AnimateAnimationNode, propertyIndex: number): AnimationConnection | undefined {
  const exact = graph.connections.find(connection =>
    connection.to.nodeId === node.id &&
    connection.to.pin === "dependencyInput" &&
    connection.to.index === propertyIndex
  );
  if (exact) {
    return exact;
  }

  const firstDependentIndex = node.properties.findIndex(property =>
    property.kind === "fitToBBox"
    || property.kind === "pin"
    || property.kind === "followPath"
    || property.kind === "orbit"
    || property.kind === "lookAt"
  );
  if (propertyIndex !== firstDependentIndex) {
    return undefined;
  }

  return graph.connections.find(connection =>
    connection.to.nodeId === node.id &&
    connection.to.pin === "dependencyInput" &&
    connection.to.index === undefined
  );
}

function fitToBBoxTargetForAnimate(graph: AnimationGraph, node: AnimateAnimationNode, property: Extract<AnimationProperty, { kind: "fitToBBox" }>, propertyIndex: number): AnimationTarget | undefined {
  const input = dependencyConnectionForAnimateProperty(graph, node, propertyIndex);
  if (input) {
    return { kind: "animationNode", nodeId: input.from.nodeId };
  }
  return property.target;
}

function pinDependencyTargetForAnimate(graph: AnimationGraph, node: AnimateAnimationNode, property: Extract<AnimationProperty, { kind: "pin" }>, propertyIndex: number): AnimationTarget | undefined {
  const input = dependencyConnectionForAnimateProperty(graph, node, propertyIndex);
  if (input) {
    return { kind: "animationNode", nodeId: input.from.nodeId };
  }
  return property.dependencyTarget;
}

function followPathTargetForAnimate(graph: AnimationGraph, node: AnimateAnimationNode, property: Extract<AnimationProperty, { kind: "followPath" }>, propertyIndex: number): AnimationTarget | undefined {
  const input = dependencyConnectionForAnimateProperty(graph, node, propertyIndex);
  if (input) {
    return { kind: "animationNode", nodeId: input.from.nodeId };
  }
  return property.pathTarget;
}

function orbitTargetForAnimate(graph: AnimationGraph, node: AnimateAnimationNode, property: Extract<AnimationProperty, { kind: "orbit" }>, propertyIndex: number): AnimationTarget | undefined {
  const input = dependencyConnectionForAnimateProperty(graph, node, propertyIndex);
  if (input) {
    return { kind: "animationNode", nodeId: input.from.nodeId };
  }
  return property.target;
}

function lookAtTargetForAnimate(graph: AnimationGraph, node: AnimateAnimationNode, property: Extract<AnimationProperty, { kind: "lookAt" }>, propertyIndex: number): AnimationTarget | undefined {
  const input = dependencyConnectionForAnimateProperty(graph, node, propertyIndex);
  if (input) {
    return { kind: "animationNode", nodeId: input.from.nodeId };
  }
  return property.target;
}

function bboxTargetRef(graph: AnimationGraph, scene: CurvaAnimationScene, target: AnimationTarget | undefined): ObjRef | undefined {
  const refs = targetRefs(graph, scene, target);
  return refs.length > 1 ? scene.groupFor(refs) : refs[0];
}

function animateNodesForMotion(graph: AnimationGraph, motion: MotionAnimationNode): AnimateAnimationNode[] {
  const nodes = graph.connections
    .filter(connection => connection.to.nodeId === motion.id && (connection.to.pin === "motionInput" || connection.to.pin === "input"))
    .sort((left, right) => (left.to.index ?? 0) - (right.to.index ?? 0))
    .flatMap(connection => {
      const node = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
      return node?.kind === "animate" ? [node] : [];
    });
  return orderAnimateNodesForDependencies(graph, nodes);
}

function animateNodesForMotionInputOrder(graph: AnimationGraph, motion: MotionAnimationNode): AnimateAnimationNode[] {
  return graph.connections
    .filter(connection => connection.to.nodeId === motion.id && (connection.to.pin === "motionInput" || connection.to.pin === "input"))
    .sort((left, right) => (left.to.index ?? 0) - (right.to.index ?? 0))
    .flatMap(connection => {
      const node = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
      return node?.kind === "animate" ? [node] : [];
    });
}

function stutterItemCountForMotion(graph: AnimationGraph, motion: MotionAnimationNode, document?: CurvaDocument): number {
  const nodes = animateNodesForMotionInputOrder(graph, motion);
  if (nodes.length > 1) {
    return nodes.length;
  }
  const singleNode = nodes[0];
  return singleNode ? groupElementCountForAnimate(graph, singleNode, document) : 0;
}

function motionHasGroupInput(graph: AnimationGraph, motion: MotionAnimationNode): boolean {
  return animateNodesForMotionInputOrder(graph, motion).some(animateNode => {
    const target = animationTargetForAnimate(graph, animateNode);
    if (target?.kind !== "animationNode") {
      return false;
    }
    return graph.nodes.some(candidate => candidate.id === target.nodeId && candidate.kind === "group");
  });
}

function groupElementCountForAnimate(graph: AnimationGraph, node: AnimateAnimationNode, document?: CurvaDocument): number {
  const target = animationTargetForAnimate(graph, node);
  if (target?.kind !== "animationNode") {
    return 1;
  }
  const targetNode = graph.nodes.find(candidate => candidate.id === target.nodeId);
  if (targetNode?.kind !== "group") {
    return 1;
  }
  return Math.max(1, groupAnimationNodeTargetCount(graph, targetNode, new Set(), document));
}

function groupAnimationNodeTargetCount(graph: AnimationGraph, node: GroupAnimationNode, seen: Set<string>, document?: CurvaDocument): number {
  if (seen.has(node.id)) {
    return 0;
  }
  const nextSeen = new Set(seen).add(node.id);
  const directCount = groupDirectTargetCount(node, document);
  const upstreamCount = graph.connections
    .filter(connection => connection.to.nodeId === node.id)
    .reduce((count, connection) => {
      const source = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
      return count + (source?.kind === "group" ? groupAnimationNodeTargetCount(graph, source, nextSeen, document) : 0);
    }, 0);
  return directCount + upstreamCount;
}

function groupDirectTargetCount(node: GroupAnimationNode, document?: CurvaDocument): number {
  if (node.regroup === "none" || node.targets.length !== 1 || !document) {
    return node.targets.length;
  }
  const target = node.targets[0];
  if (target?.kind !== "sceneNode") {
    return node.targets.length;
  }
  const sceneNode = findSceneNode(document, target.nodeId);
  if (sceneNode?.type !== "text") {
    return node.targets.length;
  }
  return splitTextNodeForRegroup(sceneNode, node.regroup).length;
}

function stutterItemsForMotion(graph: AnimationGraph, scene: CurvaAnimationScene, motion: MotionAnimationNode): Array<{ node: AnimateAnimationNode; refs?: ObjRef[]; groupOverrideContext?: GroupOverrideContext; vectorSubTargets?: VectorSubElementTarget[] }> {
  const nodes = animateNodesForMotionInputOrder(graph, motion);
  if (nodes.length > 1) {
    return nodes.map(node => ({ node }));
  }
  const singleNode = nodes[0];
  if (!singleNode) {
    return [];
  }
  const target = animationTargetForAnimate(graph, singleNode);
  const refs = targetRefs(graph, scene, target);
  const wholeNodeMembers = refs.length === 1 && "isGroup" in refs[0]! && refs[0]!.isGroup ? refs[0]!.members : [];
  const vectorSubTargets = collectVectorSubElementTargets(graph, target);
  const totalCount = wholeNodeMembers.length + vectorSubTargets.length;
  if (totalCount <= 1) {
    return [{ node: singleNode }];
  }

  const items: Array<{ node: AnimateAnimationNode; refs?: ObjRef[]; groupOverrideContext?: GroupOverrideContext; vectorSubTargets?: VectorSubElementTarget[] }> = [];
  let index = 0;
  for (const ref of wholeNodeMembers) {
    items.push({ node: singleNode, refs: [ref], groupOverrideContext: { index, count: totalCount } });
    index += 1;
  }
  for (const subTarget of vectorSubTargets) {
    items.push({ node: singleNode, vectorSubTargets: [subTarget], groupOverrideContext: { index, count: totalCount } });
    index += 1;
  }
  return items;
}

function orderAnimateNodesForDependencies(graph: AnimationGraph, nodes: AnimateAnimationNode[]): AnimateAnimationNode[] {
  if (nodes.length <= 1) {
    return nodes;
  }

  const nodeIds = new Set(nodes.map(node => node.id));
  const baseIndex = new Map(nodes.map((node, index) => [node.id, index] as const));
  const animatedTargetIds = new Map(nodes.map(node => [node.id, targetNodeIds(graph, animationTargetForAnimate(graph, node))] as const));
  const prerequisites = new Map(nodes.map(node => [node.id, new Set<string>()] as const));

  for (const dependent of nodes) {
    const dependencyTargetIds = fitDependencyTargetNodeIds(graph, dependent);
    if (dependencyTargetIds.size === 0) {
      continue;
    }
    for (const candidate of nodes) {
      if (candidate.id === dependent.id) {
        continue;
      }
      if (setsIntersect(animatedTargetIds.get(candidate.id) ?? new Set(), dependencyTargetIds)) {
        prerequisites.get(dependent.id)?.add(candidate.id);
      }
    }
  }

  const ordered: AnimateAnimationNode[] = [];
  const remaining = new Set(nodeIds);
  while (remaining.size > 0) {
    const next = [...remaining]
      .filter(nodeId => [...(prerequisites.get(nodeId) ?? [])].every(prerequisite => !remaining.has(prerequisite)))
      .sort((left, right) => (baseIndex.get(left) ?? 0) - (baseIndex.get(right) ?? 0))[0];
    if (!next) {
      ordered.push(...nodes.filter(node => remaining.has(node.id)));
      break;
    }
    const node = nodes.find(candidate => candidate.id === next);
    if (node) {
      ordered.push(node);
    }
    remaining.delete(next);
  }

  return ordered;
}

function fitDependencyTargetNodeIds(graph: AnimationGraph, node: AnimateAnimationNode): Set<string> {
  const ids = new Set<string>();
  for (const [propertyIndex, property] of node.properties.entries()) {
    if (property.kind === "fitToBBox") {
      for (const nodeId of targetNodeIds(graph, fitToBBoxTargetForAnimate(graph, node, property, propertyIndex))) {
        ids.add(nodeId);
      }
      continue;
    }
    if (property.kind === "pin") {
      for (const nodeId of targetNodeIds(graph, pinDependencyTargetForAnimate(graph, node, property, propertyIndex))) {
        ids.add(nodeId);
      }
      continue;
    }
    if (property.kind === "followPath") {
      for (const nodeId of targetNodeIds(graph, followPathTargetForAnimate(graph, node, property, propertyIndex))) {
        ids.add(nodeId);
      }
      continue;
    }
    if (property.kind === "orbit") {
      for (const nodeId of targetNodeIds(graph, orbitTargetForAnimate(graph, node, property, propertyIndex))) {
        ids.add(nodeId);
      }
      continue;
    }
    if (property.kind === "lookAt") {
      for (const nodeId of targetNodeIds(graph, lookAtTargetForAnimate(graph, node, property, propertyIndex))) {
        ids.add(nodeId);
      }
    }
  }
  return ids;
}

function targetNodeIds(graph: AnimationGraph, target: AnimationTarget | undefined, seenAnimationNodes = new Set<string>()): Set<string> {
  if (!target) {
    return new Set();
  }
  if (target.kind === "sceneNode" || isVectorSubElementAnimationTarget(target)) {
    return new Set([target.nodeId]);
  }
  if (seenAnimationNodes.has(target.nodeId)) {
    return new Set();
  }
  seenAnimationNodes.add(target.nodeId);

  const node = graph.nodes.find(candidate => candidate.id === target.nodeId);
  if (node?.kind === "group") {
    return groupTargetNodeIds(graph, node, seenAnimationNodes);
  }
  if (node?.kind === "animate") {
    return targetNodeIds(graph, animationTargetForAnimate(graph, node), seenAnimationNodes);
  }
  return new Set();
}

function groupTargetNodeIds(graph: AnimationGraph, node: GroupAnimationNode, seenAnimationNodes: Set<string>): Set<string> {
  const ids = new Set<string>();
  for (const target of node.targets) {
    for (const nodeId of targetNodeIds(graph, target, seenAnimationNodes)) {
      ids.add(nodeId);
    }
  }
  for (const connection of graph.connections.filter(connection => connection.to.nodeId === node.id)) {
    const source = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
    if (source?.kind !== "group") {
      continue;
    }
    for (const nodeId of groupTargetNodeIds(graph, source, seenAnimationNodes)) {
      ids.add(nodeId);
    }
  }
  return ids;
}

function setsIntersect(left: Set<string>, right: Set<string>): boolean {
  for (const value of left) {
    if (right.has(value)) {
      return true;
    }
  }
  return false;
}

function orderMotions(motions: MotionAnimationNode[], graph: AnimationGraph): MotionAnimationNode[] {
  if (motions.length <= 1) {
    return motions;
  }

  const byId = new Map(motions.map(motion => [motion.id, motion]));
  const nextByMotion = new Map(
    graph.connections
      .filter(connection => connection.from.pin === "motionSequenceOutput" && connection.to.pin === "motionSequenceInput")
      .map(connection => [connection.from.nodeId, connection.to.nodeId] as const),
  );
  const incoming = new Set([...nextByMotion.values()].filter(id => byId.has(id)));
  const start = motions.find(motion => !incoming.has(motion.id)) ?? motions[0]!;
  const ordered: MotionAnimationNode[] = [];
  const seen = new Set<string>();
  let current: MotionAnimationNode | undefined = start;
  while (current && !seen.has(current.id)) {
    ordered.push(current);
    seen.add(current.id);
    current = byId.get(nextByMotion.get(current.id) ?? "");
  }
  for (const motion of motions) {
    if (!seen.has(motion.id)) {
      ordered.push(motion);
    }
  }
  return ordered;
}

function motionOrder(graph: AnimationGraph): MotionAnimationNode[] {
  const allMotions = graph.nodes.filter((node): node is MotionAnimationNode => node.kind === "motion");
  const rootNodeIds = graph.nodes.filter(node => node.kind === "animation").map(node => node.id);
  if (rootNodeIds.length === 0) {
    return orderMotions(allMotions, graph);
  }
  const reachable = rootReachableMotionIds(graph, rootNodeIds);
  return orderMotions(allMotions.filter(motion => reachable.has(motion.id)), graph);
}

function motionsForRoot(graph: AnimationGraph, rootNodeId: string): MotionAnimationNode[] {
  const allMotions = graph.nodes.filter((node): node is MotionAnimationNode => node.kind === "motion");
  const reachable = rootReachableMotionIds(graph, [rootNodeId]);
  return orderMotions(allMotions.filter(m => reachable.has(m.id)), graph);
}

function rootReachableMotionIds(graph: AnimationGraph, rootNodeIds: string[]): Set<string> {
  const motionIdSet = new Set(graph.nodes.filter(node => node.kind === "motion").map(node => node.id));
  const directMotionIds = graph.connections
    .filter(connection => rootNodeIds.includes(connection.from.nodeId))
    .filter(connection => connection.from.pin === "output" && connection.to.pin === "motionSequenceInput")
    .filter(connection => motionIdSet.has(connection.to.nodeId))
    .map(connection => connection.to.nodeId);
  const reachable = new Set<string>();
  if (directMotionIds.length === 0) {
    return reachable;
  }
  const nextByMotion = new Map(
    graph.connections
      .filter(connection => connection.from.pin === "motionSequenceOutput" && connection.to.pin === "motionSequenceInput")
      .filter(connection => motionIdSet.has(connection.from.nodeId) && motionIdSet.has(connection.to.nodeId))
      .map(connection => [connection.from.nodeId, connection.to.nodeId] as const),
  );
  const queue = [...directMotionIds];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    const next = nextByMotion.get(id);
    if (next) queue.push(next);
  }
  return reachable;
}

class CurvaAnimationScene implements SceneGeometry {
  private readonly objectsByNodeId = new Map<string, CurvaSceneObject>();
  private readonly nodeById = new Map<string, SceneNode>();
  private readonly parentByNodeId = new Map<string, string>();
  private readonly zIndexByNodeId = new Map<string, number>();
  private readonly groups = new Map<string, GroupObject>();
  private readonly textRegroupRefs = new Map<string, ObjRef[]>();
  private readonly textRegroupRefsBySourceId = new Map<string, ObjRef[]>();
  private readonly animatedRotationNodeIds = new Set<string>();
  private readonly stagedStrokePaths: Array<{ refs: ObjRef[]; progress: number; inverse: boolean; erase: boolean }> = [];
  private readonly stagedVectorMutations = new Map<string, {
    vertexMoves: Map<string, { x: number; y: number; translateConnectedHandles: boolean }>;
    handleMoves: Map<string, { edgeId: string; endpoint: "from" | "to"; x: number; y: number }>;
  }>();

  constructor(private readonly document: CurvaDocument) {
    for (const page of document.pages) {
      this.indexParents(page.children, page.id);
    }
    for (const [zIndex, node] of getAllSceneNodes(document).entries()) {
      this.nodeById.set(node.id, node);
      this.objectsByNodeId.set(node.id, { id: Symbol(node.id), nodeId: node.id });
      this.zIndexByNodeId.set(node.id, zIndex);
    }
  }

  objects(): Iterable<SceneObject> {
    return this.objectsByNodeId.values();
  }

  documentSnapshot(): CurvaDocument {
    return this.document;
  }

  objectRefsForNodeId(nodeId: string): CurvaSceneObject[] {
    const object = this.objectsByNodeId.get(nodeId);
    return object ? [object] : [];
  }

  refsForSceneNodeTarget(nodeId: string): ObjRef[] {
    const object = this.objectsByNodeId.get(nodeId);
    const node = this.nodeById.get(nodeId);
    if (!object || !node) {
      return [];
    }
    return getSceneNodeChildren(node)?.length ? [this.groupFor([object])] : [object];
  }

  prepareRegroupedTextTargets(graph: AnimationGraph): void {
    for (const node of graph.nodes) {
      if (node.kind !== "group" || node.regroup === "none" || node.targets.length !== 1) {
        continue;
      }
      const target = node.targets[0];
      if (target?.kind === "sceneNode") {
        this.ensureRegroupedTextRefs(target.nodeId, node.regroup);
      }
    }
  }

  regroupTextTarget(nodeId: string, regroup: AnimationGroupRegroupMode, order?: string[]): ObjRef[] | undefined {
    const refs = this.ensureRegroupedTextRefs(nodeId, regroup);
    if (!refs) {
      return undefined;
    }
    const source = this.nodeById.get(nodeId);
    if (source) {
      source.style = { ...source.style, opacity: 0 };
    }
    for (const ref of refs) {
      const node = this.nodeById.get((ref as CurvaSceneObject).nodeId);
      if (node) {
        node.visible = true;
      }
    }
    return orderRefsByNodeId(refs, order);
  }

  private ensureRegroupedTextRefs(nodeId: string, regroup: AnimationGroupRegroupMode): ObjRef[] | undefined {
    if (regroup === "none") {
      return undefined;
    }
    const cacheKey = `${nodeId}:${regroup}`;
    const cached = this.textRegroupRefs.get(cacheKey);
    if (cached) {
      this.recordRegroupedTextRefs(nodeId, cached);
      return cached;
    }
    const source = this.nodeById.get(nodeId);
    if (source?.type !== "text") {
      return undefined;
    }
    const fragments = splitTextNodeForRegroup(source, regroup);
    if (fragments.length === 0) {
      return undefined;
    }
    this.insertTransientTextFragments(source, fragments);
    const refs = fragments.flatMap(fragment => this.objectRefsForNodeId(fragment.id));
    this.textRegroupRefs.set(cacheKey, refs);
    this.recordRegroupedTextRefs(nodeId, refs);
    return refs;
  }

  private recordRegroupedTextRefs(sourceId: string, refs: ObjRef[]): void {
    const existing = this.textRegroupRefsBySourceId.get(sourceId) ?? [];
    this.textRegroupRefsBySourceId.set(sourceId, this.uniqueRefsInOrder([...existing, ...refs]));
  }

  sceneNodeById(nodeId: string): SceneNode | undefined {
    return this.nodeById.get(nodeId);
  }

  markAnimatedRotation(refs: ObjRef[]): void {
    for (const ref of expandGroupRefs(refs)) {
      this.animatedRotationNodeIds.add((ref as CurvaSceneObject).nodeId);
    }
  }

  applyStrokePath(refs: ObjRef[], progress: number, inverse: boolean, erase: boolean): void {
    this.stagedStrokePaths.push({ refs, progress, inverse, erase });
  }

  stageVectorVertexMove(
    nodeId: string,
    vertexId: string,
    x: number,
    y: number,
    translateConnectedHandles: boolean,
  ): void {
    const batch = this.vectorMutationBatch(nodeId);
    batch.vertexMoves.set(vertexId, { x, y, translateConnectedHandles });
  }

  stageVectorHandleMove(
    nodeId: string,
    edgeId: string,
    endpoint: "from" | "to",
    x: number,
    y: number,
  ): void {
    const batch = this.vectorMutationBatch(nodeId);
    batch.handleMoves.set(`${edgeId}:${endpoint}`, { edgeId, endpoint, x, y });
  }

  applyStagedVectorMutations(): void {
    const staged = [...this.stagedVectorMutations.entries()];
    this.stagedVectorMutations.clear();
    for (const [nodeId, batch] of staged) {
      const node = this.nodeById.get(nodeId);
      if (node?.type !== "vectorNetwork") {
        continue;
      }

      for (const [vertexId, move] of batch.vertexMoves) {
        const vertex = node.network.vertices.find(candidate => candidate.id === vertexId);
        if (!vertex) {
          continue;
        }
        const dx = move.x - vertex.x;
        const dy = move.y - vertex.y;
        vertex.x = move.x;
        vertex.y = move.y;
        if (move.translateConnectedHandles) {
          translateConnectedHandles(node.network, vertexId, dx, dy);
        }
      }

      for (const move of batch.handleMoves.values()) {
        const edge = node.network.edges.find(candidate => candidate.id === move.edgeId);
        if (!edge) {
          continue;
        }
        edge[move.endpoint].handle = { x: move.x, y: move.y };
      }

      node.bounds = getVectorNetworkBounds(node.network);
    }
  }

  private vectorMutationBatch(nodeId: string): {
    vertexMoves: Map<string, { x: number; y: number; translateConnectedHandles: boolean }>;
    handleMoves: Map<string, { edgeId: string; endpoint: "from" | "to"; x: number; y: number }>;
  } {
    const existing = this.stagedVectorMutations.get(nodeId);
    if (existing) {
      return existing;
    }
    const batch = {
      vertexMoves: new Map<string, { x: number; y: number; translateConnectedHandles: boolean }>(),
      handleMoves: new Map<string, { edgeId: string; endpoint: "from" | "to"; x: number; y: number }>(),
    };
    this.stagedVectorMutations.set(nodeId, batch);
    return batch;
  }

  applyFillColor(refs: ObjRef[], fillIndex: number, colorSpace: ColorAnimationSpace, channels: string[], color: OklchColor, u: number): void {
    for (const node of this.nodesForRefs(refs)) {
      const fill = node.style.fills[fillIndex];
      if (fill?.type !== "solid") {
        continue;
      }
      const fills = [...node.style.fills];
      fills[fillIndex] = {
        ...fill,
        color: interpolateAnimatedColor(fill.color, color, colorSpace, channels, u),
      };
      node.style = { ...node.style, fills };
    }
  }

  applyStrokeColor(refs: ObjRef[], strokeIndex: number, colorSpace: ColorAnimationSpace, channels: string[], color: OklchColor, u: number): void {
    for (const node of this.nodesForRefs(refs)) {
      const strokes = normalizeStrokeStack(node.style);
      const stroke = strokes[strokeIndex];
      if (stroke?.paint.type !== "solid") {
        continue;
      }
      const nextStrokes = strokes.map((candidate, index): StrokeStyle => {
        if (index !== strokeIndex || candidate.paint.type !== "solid") {
          return candidate;
        }
        return {
          ...candidate,
          paint: {
            ...candidate.paint,
            color: interpolateAnimatedColor(candidate.paint.color, color, colorSpace, channels, u),
          },
        };
      });
      node.style = styleWithStrokeStack(node.style, nextStrokes);
    }
  }

  applyEffectValue(refs: ObjRef[], effectIndex: number, field: Extract<AnimationProperty, { kind: "effectValue" }>["field"], value: number, u: number): void {
    for (const node of this.nodesForRefs(refs)) {
      const effect = node.style.effects?.[effectIndex];
      if (!effect) {
        continue;
      }
      const nextEffect = interpolateEffectValue(effect, field, value, u);
      if (nextEffect === effect) {
        continue;
      }
      const effects = [...(node.style.effects ?? [])];
      effects[effectIndex] = nextEffect;
      node.style = { ...node.style, effects };
    }
  }

  applyEffectColor(refs: ObjRef[], effectIndex: number, colorSpace: ColorAnimationSpace, channels: string[], color: OklchColor, u: number): void {
    for (const node of this.nodesForRefs(refs)) {
      const effect = node.style.effects?.[effectIndex];
      if (!effect || !("color" in effect)) {
        continue;
      }
      const effects = [...(node.style.effects ?? [])];
      effects[effectIndex] = {
        ...effect,
        color: interpolateAnimatedColor(effect.color, color, colorSpace, channels, u),
      };
      node.style = { ...node.style, effects };
    }
  }

  applyStagedStrokePaths(): void {
    const staged = this.stagedStrokePaths.splice(0);
    if (staged.length === 0) {
      return;
    }

    type StrokeTarget = {
      node: Extract<SceneNode, { type: "vectorNetwork" }>;
      progress: number;
      inverse: boolean;
      erase: boolean;
    };
    const originals = new Map<string, VectorNetwork>();
    const bestByNodeId = new Map<string, StrokeTarget>();

    for (const stroke of staged) {
      for (const node of this.nodesForRefs(stroke.refs)) {
        if (node?.type !== "vectorNetwork") {
          continue;
        }
        if (!originals.has(node.id)) {
          originals.set(node.id, structuredClone(node.network));
        }
        const previous = bestByNodeId.get(node.id);
        // Timeline seeks can re-apply the same strokePath at u=0 and the current u.
        // Always trim from the original network and keep the furthest progress.
        if (!previous || stroke.progress >= previous.progress) {
          bestByNodeId.set(node.id, {
            node,
            progress: stroke.progress,
            inverse: stroke.inverse,
            erase: stroke.erase,
          });
        }
      }
    }

    for (const [nodeId, stroke] of bestByNodeId) {
      const original = originals.get(nodeId);
      if (!original) {
        continue;
      }
      const stroked = strokePathNetwork(original, stroke.progress, stroke.inverse, stroke.erase);
      if (stroked) {
        stroke.node.network = stroked;
      }
    }
  }

  private nodesForRefs(refs: ObjRef[]): SceneNode[] {
    const seen = new Set<string>();
    const nodes: SceneNode[] = [];
    for (const ref of expandGroupRefs(refs)) {
      const nodeId = (ref as CurvaSceneObject).nodeId;
      if (seen.has(nodeId)) {
        continue;
      }
      seen.add(nodeId);
      const node = this.nodeById.get(nodeId);
      if (node) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  frameBoundsForAnimate(graph: AnimationGraph, animateNode: AnimateAnimationNode, refs: ObjRef[]): { x: number; y: number; width: number; height: number } {
    const explicitFrameId = animationFrameIdForAnimate(graph, animateNode);
    if (explicitFrameId) {
      const explicitFrame = findSceneNode(this.document, explicitFrameId);
      if (explicitFrame?.type === "frame") {
        return { ...explicitFrame.bounds };
      }
    }

    const nodeId = firstSceneNodeId(refs);
    const parentFrame = nodeId ? this.nearestParentFrame(nodeId) : undefined;
    return parentFrame ? { ...parentFrame.bounds } : { x: 0, y: 0, width: 0, height: 0 };
  }

  groupFor(refs: ObjRef[]): GroupObject {
    const members = refs.flatMap(ref => "isGroup" in ref && ref.isGroup ? ref.members : [ref as SceneObject]);
    const key = members.map(member => (member as CurvaSceneObject).nodeId).join(",");
    const existing = this.groups.get(key);
    if (existing) {
      return existing;
    }
    const group = { id: Symbol(`group:${key}`), isGroup: true as const, members };
    this.groups.set(key, group);
    return group;
  }

  uniqueRefsInOrder(refs: ObjRef[]): ObjRef[] {
    const seen = new Set<SceneObject>();
    return refs
      .flatMap(ref => "isGroup" in ref && ref.isGroup ? ref.members : [ref as SceneObject])
      .filter(ref => {
        if (seen.has(ref)) {
          return false;
        }
        seen.add(ref);
        return true;
      });
  }

  orderRefsByDocumentZ(refs: ObjRef[]): ObjRef[] {
    const seen = new Set<SceneObject>();
    return refs
      .flatMap(ref => "isGroup" in ref && ref.isGroup ? ref.members : [ref as SceneObject])
      .filter(ref => {
        if (seen.has(ref)) {
          return false;
        }
        seen.add(ref);
        return true;
      })
      .sort((left, right) => this.zIndexForObject(left) - this.zIndexForObject(right));
  }

  getBounds(obj: SceneObject): import("@/lib/anima/types").Rect {
    const node = this.nodeById.get((obj as CurvaSceneObject).nodeId);
    const bounds = node?.bounds ?? { x: 0, y: 0, width: 0, height: 0 };
    if (node?.type === "text") {
      const visualBounds = textVisualBounds(node);
      return {
        x: visualBounds.x - bounds.x,
        y: visualBounds.y - bounds.y,
        w: visualBounds.width,
        h: visualBounds.height,
      };
    }
    return { x: 0, y: 0, w: bounds.width, h: bounds.height };
  }

  resolveAnchor(obj: SceneObject, anchor: Anchor): [number, number] {
    const b = this.getBounds(obj);
    switch (anchor) {
      case "origin":
        return this.localRotationOrigin(obj);
      case "topLeft":
        return [b.x, b.y];
      case "top":
        return [b.x + b.w / 2, b.y];
      case "topRight":
        return [b.x + b.w, b.y];
      case "left":
        return [b.x, b.y + b.h / 2];
      case "center":
        return [b.x + b.w / 2, b.y + b.h / 2];
      case "right":
        return [b.x + b.w, b.y + b.h / 2];
      case "bottomLeft":
        return [b.x, b.y + b.h];
      case "bottom":
        return [b.x + b.w / 2, b.y + b.h];
      case "bottomRight":
        return [b.x + b.w, b.y + b.h];
      case "baselineLeft":
        return [b.x, b.y + b.h];
      case "baseline":
        return [b.x + b.w / 2, b.y + b.h];
    }
  }

  getInitialTransform(obj: SceneObject): Transform {
    const node = this.nodeById.get((obj as CurvaSceneObject).nodeId);
    const rotate = ((node?.transform.rotation ?? 0) * Math.PI) / 180;
    const localOrigin = this.localRotationOrigin(obj);
    const worldOrigin = [
      (node?.bounds.x ?? 0) + localOrigin[0],
      (node?.bounds.y ?? 0) + localOrigin[1],
    ] as const;
    return {
      translate: originForLocalRotation(worldOrigin, localOrigin, rotate),
      rotate,
      scale: 1,
    };
  }

  private localRotationOrigin(obj: SceneObject): [number, number] {
    const node = this.nodeById.get((obj as CurvaSceneObject).nodeId);
    if (!node) {
      return [0, 0];
    }
    const rotationBounds = node.type === "text" ? textVisualBounds(node) : node.bounds;
    const origin = transformRotationOrigin(rotationBounds, node.transform);
    return [origin.x - node.bounds.x, origin.y - node.bounds.y];
  }

  getInitialOpacity(obj: SceneObject): number {
    const node = this.nodeById.get((obj as CurvaSceneObject).nodeId);
    const opacity = node?.style.opacity ?? 1;
    return Math.max(0, Math.min(1, Number.isFinite(opacity) ? opacity : 1));
  }

  getInitialProp(obj: SceneObject, name: string): number | undefined {
    const node = this.nodeById.get((obj as CurvaSceneObject).nodeId);
    if (!node) return undefined;
    switch (name) {
      case "cornerRadius":
        if (node.type === "primitive" && node.primitive.kind === "rectangle") {
          return node.primitive.cornerRadii?.[0] ?? 0;
        }
        return undefined;
      case "strokeWidth":
        return node.style.strokes?.[0]?.width ?? node.style.stroke?.width ?? undefined;
      case "fillL": {
        const firstFill = node.style.fills[0];
        return firstFill?.type === "solid" ? firstFill.color.l : undefined;
      }
      default:
        return undefined;
    }
  }

  applySolved(ctx: import("@/lib/anima/types").ResolvedCtx): void {
    const solvedByNodeId = new Map<string, {
      originalBounds: Rect;
      bounds: Rect;
      opacity: number;
      rotation: number;
      rotationOrigin: { x: number; y: number };
      transformChanged: boolean;
      opacityChanged: boolean;
    }>();

    for (const object of this.objectsByNodeId.values()) {
      const node = this.nodeById.get(object.nodeId);
      if (!node) {
        continue;
      }
      const transform = ctx.getTransform(object);
      const opacity = ctx.getOpacity(object);
      const scaleX = (transform.scaleX ?? 1) * transform.scale;
      const scaleY = (transform.scaleY ?? 1) * transform.scale;
      const originalBounds = { ...node.bounds };
      const bounds = this.boundsFromSolvedTransform(object, transform, originalBounds, scaleX, scaleY);
      const rotationOrigin = this.rotationOriginFromSolvedTransform(object, transform, scaleX, scaleY);
      const opacityChanged = Math.abs(opacity - this.getInitialOpacity(object)) > 0.0001;
      const transformChanged = !rectsEqual(bounds, originalBounds) || Math.abs(transform.rotate - this.getInitialTransform(object).rotate) > 0.0001;
      solvedByNodeId.set(object.nodeId, {
        originalBounds,
        bounds,
        opacity,
        rotation: (transform.rotate * 180) / Math.PI,
        rotationOrigin,
        transformChanged,
        opacityChanged,
      });
    }

    const transformedContainerAncestors = new Set<string>();
    const appliedBoundsByNodeId = new Map<string, Rect>();
    for (const object of this.objectsByNodeId.values()) {
      const node = this.nodeById.get(object.nodeId);
      const solved = solvedByNodeId.get(object.nodeId);
      if (!node || !solved) {
        continue;
      }
      if (transformedContainerAncestors.has(node.id) && !solved.transformChanged && !solved.opacityChanged) {
        continue;
      }

      const bounds = transformedContainerAncestors.has(node.id)
        ? solved.transformChanged
          ? this.transformBoundsThroughAppliedAncestors(node.id, solved.bounds, solvedByNodeId, appliedBoundsByNodeId)
          : node.bounds
        : solved.bounds;
      applyAnimatedBoundsToNode(node, bounds);
      appliedBoundsByNodeId.set(node.id, { ...bounds });
      node.transform = {
        ...node.transform,
        rotation: solved.rotation,
        x: solved.rotationOrigin.x,
        y: solved.rotationOrigin.y,
      };
      node.style = {
        ...node.style,
        opacity: solved.opacity,
      };
      applyNumericProps(node, ctx, object);
      if (solved.transformChanged && getSceneNodeChildren(node)?.length) {
        for (const descendantId of this.descendantNodeIds(node)) {
          transformedContainerAncestors.add(descendantId);
        }
      }
    }

    this.applyRegroupedSourceTransforms(solvedByNodeId, appliedBoundsByNodeId);
  }

  private applyRegroupedSourceTransforms(
    solvedByNodeId: Map<string, {
      originalBounds: Rect;
      bounds: Rect;
      opacity: number;
      rotation: number;
      rotationOrigin: { x: number; y: number };
      transformChanged: boolean;
      opacityChanged: boolean;
    }>,
    appliedBoundsByNodeId: Map<string, Rect>,
  ): void {
    for (const [sourceId, refs] of this.textRegroupRefsBySourceId) {
      const sourceSolved = solvedByNodeId.get(sourceId);
      const sourceBounds = appliedBoundsByNodeId.get(sourceId) ?? sourceSolved?.bounds;
      if (!sourceSolved || !sourceBounds || rectsEqual(sourceSolved.originalBounds, sourceBounds)) {
        continue;
      }
      for (const ref of refs) {
        const fragment = this.nodeById.get((ref as CurvaSceneObject).nodeId);
        if (!fragment) {
          continue;
        }
        applyAnimatedBoundsToNode(fragment, transformBounds(fragment.bounds, sourceSolved.originalBounds, sourceBounds));
      }
    }
  }

  private transformBoundsThroughAppliedAncestors(
    nodeId: string,
    bounds: Rect,
    solvedByNodeId: Map<string, { originalBounds: Rect; bounds: Rect }>,
    appliedBoundsByNodeId: Map<string, Rect>,
  ): Rect {
    const ancestors: string[] = [];
    let parentId = this.parentByNodeId.get(nodeId);
    while (parentId) {
      ancestors.unshift(parentId);
      parentId = this.parentByNodeId.get(parentId);
    }

    return ancestors.reduce((nextBounds, ancestorId) => {
      const solved = solvedByNodeId.get(ancestorId);
      const appliedBounds = appliedBoundsByNodeId.get(ancestorId);
      return solved && appliedBounds
        ? transformBounds(nextBounds, solved.originalBounds, appliedBounds)
        : nextBounds;
    }, bounds);
  }

  private boundsFromSolvedTransform(
    object: SceneObject,
    transform: Transform,
    originalBounds: SceneNode["bounds"],
    scaleX: number,
    scaleY: number,
  ): SceneNode["bounds"] {
    const width = Math.max(0.01, originalBounds.width * scaleX);
    const height = Math.max(0.01, originalBounds.height * scaleY);
    const localCenter = this.resolveAnchor(object, "center");
    const solvedCenter = transformLocalPoint(transform, localCenter, scaleX, scaleY);
    const localBounds = this.getBounds(object);
    const visualCenterOffset = {
      x: (localCenter[0] - localBounds.x) * scaleX,
      y: (localCenter[1] - localBounds.y) * scaleY,
    };

    return {
      x: solvedCenter.x - visualCenterOffset.x,
      y: solvedCenter.y - visualCenterOffset.y,
      width,
      height,
    };
  }

  private rotationOriginFromSolvedTransform(
    object: SceneObject,
    transform: Transform,
    scaleX: number,
    scaleY: number,
  ): { x: number; y: number } {
    const nodeId = (object as CurvaSceneObject).nodeId;
    const localOrigin = this.animatedRotationNodeIds.has(nodeId)
      ? this.resolveAnchor(object, "center")
      : this.localRotationOrigin(object);
    return transformLocalPoint(transform, localOrigin, scaleX, scaleY);
  }

  private nearestParentFrame(nodeId: string): SceneNode | undefined {
    let parentId = this.parentByNodeId.get(nodeId);
    while (parentId) {
      const parent = findSceneNode(this.document, parentId);
      if (parent?.type === "frame") {
        return parent;
      }
      parentId = this.parentByNodeId.get(parentId);
    }
    return undefined;
  }

  private indexParents(children: SceneNode[], parentId: string): void {
    for (const child of children) {
      this.parentByNodeId.set(child.id, parentId);
      const nested = getSceneNodeChildren(child);
      if (nested) {
        this.indexParents(nested, child.id);
      }
    }
  }

  private insertTransientTextFragments(source: TextNode, fragments: TextNode[]): void {
    const parentId = this.parentByNodeId.get(source.id);
    const siblings = parentId ? this.childrenForParent(parentId) : undefined;
    const sourceIndex = siblings?.findIndex(child => child.id === source.id) ?? -1;
    if (siblings && sourceIndex >= 0) {
      siblings.splice(sourceIndex + 1, 0, ...fragments);
    } else {
      this.document.pages[0]?.children.push(...fragments);
    }

    const baseZ = this.zIndexByNodeId.get(source.id) ?? this.zIndexByNodeId.size;
    for (const [index, fragment] of fragments.entries()) {
      this.nodeById.set(fragment.id, fragment);
      this.objectsByNodeId.set(fragment.id, { id: Symbol(fragment.id), nodeId: fragment.id });
      this.zIndexByNodeId.set(fragment.id, baseZ + (index + 1) / Math.max(1, fragments.length + 1));
      this.parentByNodeId.set(fragment.id, parentId ?? this.document.pages[0]?.id ?? "");
    }
  }

  private childrenForParent(parentId: string): SceneNode[] | undefined {
    const page = this.document.pages.find(candidate => candidate.id === parentId);
    if (page) {
      return page.children;
    }
    const parent = this.nodeById.get(parentId);
    return parent ? getSceneNodeChildren(parent) : undefined;
  }

  private descendantObjects(children: SceneNode[]): SceneObject[] {
    return children.flatMap(child => {
      const object = this.objectsByNodeId.get(child.id);
      const nested = getSceneNodeChildren(child);
      return [
        ...(object ? [object] : []),
        ...(nested ? this.descendantObjects(nested) : []),
      ];
    });
  }

  private descendantNodeIds(node: SceneNode): string[] {
    const children = getSceneNodeChildren(node);
    if (!children) {
      return [];
    }
    return children.flatMap(child => [child.id, ...this.descendantNodeIds(child)]);
  }

  private zIndexForObject(object: SceneObject): number {
    return this.zIndexByNodeId.get((object as CurvaSceneObject).nodeId) ?? Number.MAX_SAFE_INTEGER;
  }
}

export function splitTextNodeForRegroup(source: TextNode, regroup: Exclude<AnimationGroupRegroupMode, "none">): TextNode[] {
  const layout = textLayoutForRegroup(source);
  const offsetY = verticalTextOffset(source.bounds.height, layout.height, source.text.style.verticalAlign);
  const fragments: TextNode[] = [];
  for (const [runIndex, run] of layout.runs.entries()) {
    for (const range of regroupRangesForRun(run, regroup)) {
      const characters = run.text.slice(range.startOffset, range.endOffset);
      if (!characters || (regroup !== "symbols" && characters.trim().length === 0)) {
        continue;
      }
      const width = Math.max(1, advanceForRunRange(run, range.startOffset, range.endOffset - range.startOffset));
      fragments.push({
        ...structuredClone(source),
        id: `${source.id}:regroup:${regroup}:${runIndex}:${run.start + range.startOffset}:${run.start + range.endOffset}`,
        name: characters,
        visible: false,
        locked: source.locked,
        bounds: {
          x: source.bounds.x + run.x + advanceForRunRange(run, 0, range.startOffset),
          y: source.bounds.y + offsetY + run.y,
          width,
          height: run.lineHeight,
        },
        style: { ...structuredClone(source.style) },
        text: sliceRichText(source.text, run.start + range.startOffset, run.start + range.endOffset),
      });
    }
  }
  return fragments;
}

function orderRefsByNodeId(refs: ObjRef[], order: string[] | undefined): ObjRef[] {
  if (!order?.length) {
    return refs;
  }
  const byNodeId = new Map(refs.map(ref => [(ref as CurvaSceneObject).nodeId, ref] as const));
  const ordered: ObjRef[] = [];
  const seen = new Set<string>();
  for (const nodeId of order) {
    const ref = byNodeId.get(nodeId);
    if (ref && !seen.has(nodeId)) {
      ordered.push(ref);
      seen.add(nodeId);
    }
  }
  for (const ref of refs) {
    const nodeId = (ref as CurvaSceneObject).nodeId;
    if (!seen.has(nodeId)) {
      ordered.push(ref);
    }
  }
  return ordered;
}

function textLayoutForRegroup(source: TextNode): { height: number; runs: TextLayoutRun[] } {
  if (typeof globalThis.document !== "undefined" && typeof globalThis.document.createElement === "function") {
    return getCachedTextLayout(source, 10);
  }
  return fallbackTextLayout(source);
}

function fallbackTextLayout(source: TextNode): { height: number; runs: TextLayoutRun[] } {
  const style = source.text.style;
  const lineHeight = typeof style.lineHeight === "object"
    ? style.lineHeight.unit === "px" ? style.lineHeight.value : style.fontSize * (style.lineHeight.value / 100)
    : style.lineHeight === "auto" ? style.fontSize * 1.25 : style.lineHeight;
  const runs: TextLayoutRun[] = [];
  let start = 0;
  for (const [lineIndex, line] of source.text.characters.split("\n").entries()) {
    const advances = Array.from(line).map((character, index) => (
      (character.trim() ? style.fontSize * 0.6 : style.fontSize * 0.32) +
      (index < Array.from(line).length - 1 ? style.letterSpacing : 0)
    ));
    runs.push({
      text: line,
      style,
      x: 0,
      y: lineIndex * lineHeight,
      width: advances.reduce((sum, advance) => sum + advance, 0),
      start,
      end: start + line.length,
      advances,
      lineHeight,
      baseline: style.fontSize * 0.82,
      fill: style.fill,
    });
    start += line.length + 1;
  }
  return { height: Math.max(lineHeight, runs.length * lineHeight), runs };
}

function regroupRangesForRun(run: TextLayoutRun, regroup: Exclude<AnimationGroupRegroupMode, "none">): Array<{ startOffset: number; endOffset: number }> {
  if (regroup === "words") {
    return [...run.text.matchAll(/\S+/g)].map(match => ({
      startOffset: match.index ?? 0,
      endOffset: (match.index ?? 0) + match[0].length,
    }));
  }

  const ranges: Array<{ startOffset: number; endOffset: number }> = [];
  let offset = 0;
  for (const symbol of Array.from(run.text)) {
    const endOffset = offset + symbol.length;
    if (regroup === "symbols" || symbol.trim().length > 0) {
      ranges.push({ startOffset: offset, endOffset });
    }
    offset = endOffset;
  }
  return ranges;
}

function sliceRichText(text: RichTextModel, start: number, end: number): RichTextModel {
  const safeStart = Math.max(0, Math.min(start, end, text.characters.length));
  const safeEnd = Math.max(safeStart, Math.min(Math.max(start, end), text.characters.length));
  return {
    ...structuredClone(text),
    characters: text.characters.slice(safeStart, safeEnd),
    characterStyleOverrides: text.characterStyleOverrides.slice(safeStart, safeEnd),
    lineTypes: ["NONE"],
    lineIndentations: [0],
    textLayout: "AUTO_WIDTH",
    style: {
      ...structuredClone(text.style),
      horizontalAlign: "LEFT",
      verticalAlign: "TOP",
    },
  };
}

function advanceForRunRange(run: TextLayoutRun, start: number, length: number): number {
  return run.advances.slice(start, start + length).reduce((sum, advance) => sum + advance, 0);
}

function transformLocalPoint(transform: Transform, point: readonly [number, number], scaleX: number, scaleY: number): { x: number; y: number } {
  const cos = Math.cos(transform.rotate);
  const sin = Math.sin(transform.rotate);
  const x = point[0] * scaleX;
  const y = point[1] * scaleY;
  return {
    x: transform.translate[0] + x * cos - y * sin,
    y: transform.translate[1] + x * sin + y * cos,
  };
}

function originForLocalRotation(worldOrigin: readonly [number, number], localOrigin: readonly [number, number], rotate: number): [number, number] {
  const cos = Math.cos(rotate);
  const sin = Math.sin(rotate);
  return [
    worldOrigin[0] - (localOrigin[0] * cos - localOrigin[1] * sin),
    worldOrigin[1] - (localOrigin[0] * sin + localOrigin[1] * cos),
  ];
}

function applyAnimatedBoundsToNode(node: SceneNode, bounds: Rect): void {
  const fromBounds = { ...node.bounds };
  if (rectsEqual(fromBounds, bounds)) {
    return;
  }

  if (node.type === "vectorNetwork") {
    transformVectorNetwork(node, fromBounds, bounds);
    transformNodeRotationOrigin(node, fromBounds, bounds);
    node.bounds = getVectorNetworkBounds(node.network);
    return;
  }

  if (node.type === "shape") {
    node.path = contoursToPathData(transformContours(pathDataToContours(node.path), fromBounds, bounds));
  }

  if (node.type === "boolean") {
    const transformed = transformContours(node.resolved.contours, fromBounds, bounds);
    node.resolved = {
      contours: transformed,
      path: contoursToPathData(transformed),
    };
    for (const source of node.sourceNodes) {
      applyAnimatedBoundsToNode(source, transformBounds(source.bounds, fromBounds, bounds));
    }
  }

  const children = getSceneNodeChildren(node);
  if (children && node.type !== "boolean") {
    for (const child of children) {
      applyAnimatedBoundsToNode(child, transformBounds(child.bounds, fromBounds, bounds));
    }
  }

  if (node.type === "text") {
    const scaleX = fromBounds.width === 0 ? 1 : bounds.width / fromBounds.width;
    const scaleY = fromBounds.height === 0 ? 1 : bounds.height / fromBounds.height;
    scaleTextTypography(node, textScaleForTypography(scaleX, scaleY));
  }

  transformNodeRotationOrigin(node, fromBounds, bounds);
  node.bounds = { ...bounds };
}

function transformNodeRotationOrigin(node: SceneNode, fromBounds: Rect, toBounds: Rect): void {
  if (node.transform.rotation === 0) {
    return;
  }
  const transformPoint = pointTransformer(fromBounds, toBounds);
  const nextOrigin = transformPoint(transformRotationOrigin(fromBounds, node.transform));
  node.transform = { ...node.transform, x: nextOrigin.x, y: nextOrigin.y };
}

function transformVectorNetwork(node: Extract<SceneNode, { type: "vectorNetwork" }>, fromBounds: Rect, toBounds: Rect): void {
  const transformPoint = pointTransformer(fromBounds, toBounds);

  for (const vertex of node.network.vertices) {
    const next = transformPoint(vertex);
    vertex.x = next.x;
    vertex.y = next.y;
  }

  for (const edge of node.network.edges) {
    if (edge.from.handle) {
      edge.from.handle = transformPoint(edge.from.handle);
    }
    if (edge.to.handle) {
      edge.to.handle = transformPoint(edge.to.handle);
    }
  }
}

function transformBounds(bounds: Rect, fromGroup: Rect, toGroup: Rect): Rect {
  const transformPoint = pointTransformer(fromGroup, toGroup);
  const topLeft = transformPoint({ x: bounds.x, y: bounds.y });
  const bottomRight = transformPoint({ x: bounds.x + bounds.width, y: bounds.y + bounds.height });
  return {
    x: topLeft.x,
    y: topLeft.y,
    width: Math.max(0, bottomRight.x - topLeft.x),
    height: Math.max(0, bottomRight.y - topLeft.y),
  };
}

function pointTransformer(fromBounds: Rect, toBounds: Rect): (point: { x: number; y: number }) => { x: number; y: number } {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  return point => ({
    x: toBounds.x + (point.x - fromBounds.x) * scaleX,
    y: toBounds.y + (point.y - fromBounds.y) * scaleY,
  });
}

function rectsEqual(left: Rect, right: Rect): boolean {
  return (
    Math.abs(left.x - right.x) < 0.0001 &&
    Math.abs(left.y - right.y) < 0.0001 &&
    Math.abs(left.width - right.width) < 0.0001 &&
    Math.abs(left.height - right.height) < 0.0001
  );
}

function textScaleForTypography(scaleX: number, scaleY: number): number {
  const normalizedX = Number.isFinite(scaleX) ? Math.abs(scaleX) : 1;
  const normalizedY = Number.isFinite(scaleY) ? Math.abs(scaleY) : 1;
  return (normalizedX + normalizedY) / 2;
}

function scaleTextTypography(node: Extract<SceneNode, { type: "text" }>, scale: number): void {
  if (!Number.isFinite(scale) || Math.abs(scale - 1) < 0.0001) {
    return;
  }
  node.text = {
    ...node.text,
    style: scaleTypeStyle(node.text.style, scale),
    styleOverrideTable: Object.fromEntries(
      Object.entries(node.text.styleOverrideTable).map(([id, style]) => [id, scalePartialTypeStyle(style, scale)]),
    ),
  };
}

function scaleTypeStyle(style: TypeStyle, scale: number): TypeStyle {
  return {
    ...style,
    fontSize: style.fontSize * scale,
    letterSpacing: style.letterSpacing * scale,
    lineHeight: scaleLineHeight(style.lineHeight, scale),
  };
}

function scalePartialTypeStyle(style: Partial<TypeStyle>, scale: number): Partial<TypeStyle> {
  const scaled = { ...style };
  if (scaled.fontSize !== undefined) {
    scaled.fontSize *= scale;
  }
  if (scaled.letterSpacing !== undefined) {
    scaled.letterSpacing *= scale;
  }
  if (scaled.lineHeight !== undefined) {
    scaled.lineHeight = scaleLineHeight(scaled.lineHeight, scale);
  }
  return scaled;
}

function scaleLineHeight(lineHeight: TextLineHeight, scale: number): TextLineHeight {
  return lineHeight !== "auto" && lineHeight.unit === "px"
    ? { ...lineHeight, value: lineHeight.value * scale }
    : lineHeight;
}

function normalizeOpacityValue(value: number): number {
  const normalized = value > 1 ? value / 100 : value;
  return Math.max(0, Math.min(1, Number.isFinite(normalized) ? normalized : 1));
}

function followPathPointExpr(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  property: Extract<AnimationProperty, { kind: "followPath" }>,
  propertyIndex: number,
  u: number,
): Vec2Expr | undefined {
  const pathRef = pathObjectRefForTarget(graph, scene, followPathTargetForAnimate(graph, node, property, propertyIndex));
  if (!pathRef) {
    return undefined;
  }
  return followPathWorldPointExpr(ctx, scene, pathRef, scaledPathProgress(u, property.progress));
}

function applyFollowPathOrientation(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  refs: ObjRef[],
  selection: ReturnType<AnimCtxImpl["select"]>,
  property: Extract<AnimationProperty, { kind: "followPath" }>,
  propertyIndex: number,
  u: number,
  referenceAngle: number,
): void {
  const pathRef = pathObjectRefForTarget(graph, scene, followPathTargetForAnimate(graph, node, property, propertyIndex));
  if (!pathRef) {
    return;
  }
  const tangentAngle = followPathWorldTangentAngle(
    ctx,
    scene,
    pathRef,
    scaledPathProgress(u, property.progress),
  );
  if (tangentAngle === undefined) {
    return;
  }

  scene.markAnimatedRotation(refs);
  pinSingleObjectRotationCenter(scene, ctx, refs);
  selection.rotateBy(shortestAngleDeltaRadians(referenceAngle, tangentAngle), 1);
}

function pathObjectRefForTarget(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  target: AnimationTarget | undefined,
): SceneObject | undefined {
  const nodeIds = targetNodeIds(graph, target);
  if (nodeIds.size !== 1) {
    return undefined;
  }
  const pathNodeId = [...nodeIds][0]!;
  return scene.objectRefsForNodeId(pathNodeId)[0] as SceneObject | undefined;
}

function followPathWorldPointExpr(
  ctx: AnimCtxImpl,
  scene: CurvaAnimationScene,
  pathRef: SceneObject,
  progress: number,
): Vec2Expr {
  const nodeId = (pathRef as CurvaSceneObject).nodeId;
  return () => {
    const node = scene.sceneNodeById(nodeId);
    if (node?.type !== "vectorNetwork") {
      return [0, 0];
    }
    const pathPoint = pointOnFollowPath(node.network, progress);
    if (!pathPoint) {
      return [0, 0];
    }
    const localPoint: [number, number] = [
      pathPoint.x - node.bounds.x,
      pathPoint.y - node.bounds.y,
    ];
    const resolved = ctx.solve(scene);
    const transform = resolved.getTransform(pathRef);
    const scaleX = (transform.scaleX ?? 1) * transform.scale;
    const scaleY = (transform.scaleY ?? 1) * transform.scale;
    const world = transformLocalPoint(transform, localPoint, scaleX, scaleY);
    return [world.x, world.y];
  };
}

function followPathWorldTangentAngle(
  ctx: AnimCtxImpl,
  scene: CurvaAnimationScene,
  pathRef: SceneObject,
  progress: number,
): number | undefined {
  const node = scene.sceneNodeById((pathRef as CurvaSceneObject).nodeId);
  if (node?.type !== "vectorNetwork") {
    return undefined;
  }
  const tangent = tangentOnFollowPath(node.network, progress);
  if (!tangent) {
    return undefined;
  }

  const transform = ctx.solve(scene).getTransform(pathRef);
  const scaleX = (transform.scaleX ?? 1) * transform.scale;
  const scaleY = (transform.scaleY ?? 1) * transform.scale;
  const localX = tangent.x * scaleX;
  const localY = tangent.y * scaleY;
  const cos = Math.cos(transform.rotate);
  const sin = Math.sin(transform.rotate);
  const worldX = localX * cos - localY * sin;
  const worldY = localX * sin + localY * cos;
  return Math.hypot(worldX, worldY) > 1e-9 ? Math.atan2(worldY, worldX) : undefined;
}

function scaledPathProgress(u: number, progress: number | undefined): number {
  return Math.max(0, Math.min(1, u * (Number.isFinite(progress) ? progress ?? 1 : 1)));
}

function orbitTargetPoint(
  ctx: AnimCtxImpl,
  selection: ReturnType<AnimCtxImpl["select"]>,
  orbitTarget: ObjRef,
  angleDegrees: number,
  u: number,
): (resolved: import("@/lib/anima/types").ResolvedCtx) => [number, number] {
  const targetCenter = selection.bbox().center();
  const orbitCenter = ctx.bbox(orbitTarget).center();
  const angle = (angleDegrees * Math.PI / 180) * u;
  return resolved => {
    const center = orbitCenter(resolved);
    const target = targetCenter(resolved);
    const dx = target[0] - center[0];
    const dy = target[1] - center[1];
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return [
      center[0] + dx * cos - dy * sin,
      center[1] + dx * sin + dy * cos,
    ];
  };
}

function applyLookAt(
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  refs: ObjRef[],
  selection: ReturnType<AnimCtxImpl["select"]>,
  target: ObjRef,
  referenceAngle: number,
): void {
  // Position constraints are expressions so that dependencies can use the live
  // solved scene. Resolve them before measuring the final source→target bearing.
  ctx.resolveDeferredPins(scene);
  const source = refs.length > 1 ? scene.groupFor(refs) : refs[0]!;
  const sourceCenter = ctx.anchorPosition(source, "center");
  const targetCenter = ctx.anchorPosition(target, "center");
  const dx = targetCenter[0] - sourceCenter[0];
  const dy = targetCenter[1] - sourceCenter[1];
  if (Math.hypot(dx, dy) <= 1e-9) {
    return;
  }

  const currentAngle = Math.atan2(dy, dx);
  const delta = shortestAngleDeltaRadians(referenceAngle, currentAngle);
  scene.markAnimatedRotation(refs);
  pinSingleObjectRotationCenter(scene, ctx, refs);
  selection.rotateBy(delta, 1);
}

function shortestAngleDeltaRadians(from: number, to: number): number {
  const tau = Math.PI * 2;
  const delta = ((to - from) % tau + tau) % tau;
  return delta > Math.PI ? delta - tau : delta;
}

function pinTargetPoint(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  node: AnimateAnimationNode,
  property: Extract<AnimationProperty, { kind: "pin" }>,
  propertyIndex: number,
  frameBounds: { x: number; y: number; width: number; height: number },
): import("@/lib/anima/types").Vec2Expr {
  const pinDependencyTarget = pinDependencyTargetForAnimate(graph, node, property, propertyIndex);
  const pathPoint = pinPathTargetPoint(graph, scene, ctx, property, pinDependencyTarget);
  if (pathPoint) {
    return pathPoint;
  }

  const dependencyTarget = bboxTargetRef(graph, scene, pinDependencyTarget);
  if (dependencyTarget) {
    const anchor = property.target?.kind === "frameAnchor"
      ? property.target.anchor
      : isDistancePinTarget(property.target)
        ? "center"
        : "topLeft";
    const targetAnchor = ctx.bbox(dependencyTarget).anchor(anchor as Anchor);
    return ctx => {
      const point = targetAnchor(ctx);
      return isDistancePinTarget(property.target)
        ? [point[0], point[1]]
        : [point[0] + property.x, point[1] + property.y];
    };
  }

  if (property.target?.kind === "frameAnchor") {
    const anchor = frameAnchorPoint(frameBounds, property.target.anchor);
    return [anchor[0] + property.x, anchor[1] + property.y];
  }
  if (isDistancePinTarget(property.target)) {
    return frameAnchorPoint(frameBounds, "center");
  }
  return [frameBounds.x + property.x, frameBounds.y + property.y];
}

type DistancePinMode = "distance" | "minDistance" | "maxDistance";

function initialDistanceDependencyCenter(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  node: AnimateAnimationNode,
  property: Extract<AnimationProperty, { kind: "pin" }>,
  propertyIndex: number,
  frameBounds: { x: number; y: number; width: number; height: number },
): [number, number] {
  const pinDependencyTarget = pinDependencyTargetForAnimate(graph, node, property, propertyIndex);
  const dependencyTarget = bboxTargetRef(graph, scene, pinDependencyTarget);
  if (!dependencyTarget) {
    return frameAnchorPoint(frameBounds, "center");
  }
  return initialCenterForObjRef(scene, dependencyTarget);
}

function initialCenterForObjRef(scene: CurvaAnimationScene, ref: ObjRef): [number, number] {
  if ("isGroup" in ref && ref.isGroup) {
    const members = expandGroupRefs(ref.members);
    if (members.length === 0) {
      return [0, 0];
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const member of members) {
      const node = scene.sceneNodeById((member as CurvaSceneObject).nodeId);
      const bounds = node?.bounds ?? { x: 0, y: 0, width: 0, height: 0 };
      minX = Math.min(minX, bounds.x);
      minY = Math.min(minY, bounds.y);
      maxX = Math.max(maxX, bounds.x + bounds.width);
      maxY = Math.max(maxY, bounds.y + bounds.height);
    }
    return [(minX + maxX) / 2, (minY + maxY) / 2];
  }
  const node = scene.sceneNodeById((ref as CurvaSceneObject).nodeId);
  const bounds = node?.bounds ?? { x: 0, y: 0, width: 0, height: 0 };
  return [bounds.x + bounds.width / 2, bounds.y + bounds.height / 2];
}

function pointAtDistanceExpr(
  sourceExpr: import("@/lib/anima/types").Vec2Expr,
  targetExpr: import("@/lib/anima/types").Vec2Expr,
  distance: number,
  mode: DistancePinMode = "distance",
): import("@/lib/anima/types").Vec2Expr {
  return resolved => {
    const source = typeof sourceExpr === "function" ? sourceExpr(resolved) : sourceExpr;
    const target = typeof targetExpr === "function" ? targetExpr(resolved) : targetExpr;
    return pointAtDistance(source, target, distance, mode);
  };
}

function pointAtIntegratedDistanceExpr(
  sourceExpr: import("@/lib/anima/types").Vec2Expr,
  targetInitial: readonly [number, number],
  targetExpr: import("@/lib/anima/types").Vec2Expr,
  distance: number,
  mode: "minDistance" | "maxDistance",
): import("@/lib/anima/types").Vec2Expr {
  return resolved => {
    const source = typeof sourceExpr === "function" ? sourceExpr(resolved) : sourceExpr;
    const target = typeof targetExpr === "function" ? targetExpr(resolved) : targetExpr;
    return integrateDistanceConstraint(source, targetInitial, target, distance, mode);
  };
}

function integrateDistanceConstraint(
  sourceInitial: readonly [number, number],
  targetInitial: readonly [number, number],
  targetCurrent: readonly [number, number],
  distance: number,
  mode: "minDistance" | "maxDistance",
): [number, number] {
  const travel = Math.hypot(targetCurrent[0] - targetInitial[0], targetCurrent[1] - targetInitial[1]);
  if (travel <= 1e-9) {
    return pointAtDistance(sourceInitial, targetCurrent, distance, mode);
  }

  // Walk the dependency path in fixed arc-length steps. Using i/N of the current
  // travel would re-space every earlier sample whenever the dependency moves
  // farther, which makes the discrete closest approach keep shifting and causes
  // a slow drift for the rest of the motion.
  const stepLength = Math.max(1, Math.min(4, Math.max(0, distance) * 0.05 || 2));
  let point: [number, number] = [sourceInitial[0], sourceInitial[1]];
  let distanceAlong = 0;
  while (distanceAlong < travel) {
    distanceAlong = Math.min(travel, distanceAlong + stepLength);
    const t = distanceAlong / travel;
    const target: [number, number] = [
      targetInitial[0] + (targetCurrent[0] - targetInitial[0]) * t,
      targetInitial[1] + (targetCurrent[1] - targetInitial[1]) * t,
    ];
    point = pointAtDistance(point, target, distance, mode);
  }
  return point;
}

function pointAtDistance(
  source: readonly [number, number],
  target: readonly [number, number],
  distance: number,
  mode: DistancePinMode = "distance",
): [number, number] {
  const dx = source[0] - target[0];
  const dy = source[1] - target[1];
  const length = Math.hypot(dx, dy);
  const clampedDistance = Math.max(0, distance);

  // Min: only push out when closer than the limit. Max: only pull in when farther.
  // Exact distance always lands on the requested length.
  if (mode === "minDistance" && length >= clampedDistance) {
    return [source[0], source[1]];
  }
  if (mode === "maxDistance" && length <= clampedDistance) {
    return [source[0], source[1]];
  }

  const scale = clampedDistance / (length > 1e-9 ? length : 1);
  return length > 1e-9
    ? [target[0] + dx * scale, target[1] + dy * scale]
    : [target[0] + clampedDistance, target[1]];
}

function pinPathTargetPoint(
  graph: AnimationGraph,
  scene: CurvaAnimationScene,
  ctx: AnimCtxImpl,
  property: Extract<AnimationProperty, { kind: "pin" }>,
  dependencyTarget: AnimationTarget | undefined,
): Vec2Expr | undefined {
  if (property.target?.kind !== "pathPercent") {
    return undefined;
  }
  const pathRef = pathObjectRefForTarget(graph, scene, dependencyTarget);
  if (!pathRef) {
    return undefined;
  }
  return followPathWorldPointExpr(ctx, scene, pathRef, property.target.progress);
}

function frameAnchorPoint(bounds: { x: number; y: number; width: number; height: number }, anchor: Anchor): [number, number] {
  switch (anchor) {
    case "origin":
    case "topLeft":
      return [bounds.x, bounds.y];
    case "top":
      return [bounds.x + bounds.width / 2, bounds.y];
    case "topRight":
      return [bounds.x + bounds.width, bounds.y];
    case "left":
      return [bounds.x, bounds.y + bounds.height / 2];
    case "center":
      return [bounds.x + bounds.width / 2, bounds.y + bounds.height / 2];
    case "right":
      return [bounds.x + bounds.width, bounds.y + bounds.height / 2];
    case "bottomLeft":
      return [bounds.x, bounds.y + bounds.height];
    case "bottom":
    case "baseline":
      return [bounds.x + bounds.width / 2, bounds.y + bounds.height];
    case "bottomRight":
      return [bounds.x + bounds.width, bounds.y + bounds.height];
    case "baselineLeft":
      return [bounds.x, bounds.y + bounds.height];
  }
}

function animationFrameIdForAnimate(graph: AnimationGraph, animateNode: AnimateAnimationNode): string | undefined {
  const upstream = animationFrameIdReachableUpstream(graph, animateNode.id, new Set());
  if (upstream) {
    return upstream;
  }

  const motionIds = new Set(
    graph.connections
      .filter(connection => connection.from.nodeId === animateNode.id)
      .map(connection => connection.to.nodeId),
  );
  const motionRoot = graph.connections
    .map(connection => graph.nodes.find(node => node.id === connection.from.nodeId && node.kind === "animation" && motionIds.has(connection.to.nodeId)))
    .find(Boolean);
  if (motionRoot?.kind === "animation" && motionRoot.frameId) {
    return motionRoot.frameId;
  }

  const rootsWithFrames = graph.nodes.filter(node => node.kind === "animation" && node.frameId);
  return rootsWithFrames.length === 1 && rootsWithFrames[0]?.kind === "animation" ? rootsWithFrames[0].frameId : undefined;
}

function animationFrameIdReachableUpstream(graph: AnimationGraph, nodeId: string, visited: Set<string>): string | undefined {
  if (visited.has(nodeId)) {
    return undefined;
  }
  visited.add(nodeId);
  for (const connection of graph.connections.filter(candidate => candidate.to.nodeId === nodeId)) {
    const fromNode = graph.nodes.find(node => node.id === connection.from.nodeId);
    if (fromNode?.kind === "animation" && fromNode.frameId) {
      return fromNode.frameId;
    }
    const nested = animationFrameIdReachableUpstream(graph, connection.from.nodeId, visited);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

function firstSceneNodeId(refs: ObjRef[]): string | undefined {
  return sceneNodeIdsForRefs(refs)[0];
}

function sceneNodeIdsForRefs(refs: ObjRef[]): string[] {
  const nodeIds: string[] = [];
  for (const ref of refs) {
    if ("isGroup" in ref && ref.isGroup) {
      nodeIds.push(...sceneNodeIdsForRefs(ref.members));
      continue;
    }
    const nodeId = (ref as Partial<CurvaSceneObject>).nodeId;
    if (nodeId) {
      nodeIds.push(nodeId);
    }
  }
  return nodeIds;
}

function applyNumericProps(node: SceneNode, ctx: import("@/lib/anima/types").ResolvedCtx, object: SceneObject): void {
  const cornerRadius = ctx.getProp(object, "cornerRadius");
  if (cornerRadius !== undefined && node.type === "primitive" && node.primitive.kind === "rectangle") {
    node.primitive = {
      ...node.primitive,
      cornerRadii: [cornerRadius, cornerRadius, cornerRadius, cornerRadius],
    };
  }

  const strokeWidth = ctx.getProp(object, "strokeWidth");
  if (strokeWidth !== undefined && node.style.strokes?.[0]) {
    node.style = {
      ...node.style,
      strokes: [{ ...node.style.strokes[0], width: Math.max(0, strokeWidth) }, ...node.style.strokes.slice(1)],
      stroke: node.style.stroke ? { ...node.style.stroke, width: Math.max(0, strokeWidth) } : node.style.stroke,
    };
  }

  const fillL = ctx.getProp(object, "fillL");
  const firstFill = node.style.fills[0];
  if (fillL !== undefined && firstFill?.type === "solid") {
    node.style = {
      ...node.style,
      fills: [{ ...firstFill, color: { ...firstFill.color, l: Math.max(0, Math.min(1, fillL)) } }, ...node.style.fills.slice(1)],
    };
  }
}
